/**
 * CanvasView — a two-layer editor for non-technical authors.
 *
 * Layer 1 (剧集地图): a route map derived from every scene's `.ls`, useful for
 * route structure, dangling targets, and jumping between episodes.
 *
 * Layer 2 (剧内路线): the selected episode as grouped story blocks. Consecutive
 * non-branching beats are intentionally folded into one editable card, while
 * choices, phone moments, gates, pauses, and state events remain visible as
 * distinct structure. The right inspector edits only the selected block/step.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Panel,
  MarkerType,
  ConnectionMode,
  PanOnScrollMode,
  useReactFlow,
  useViewport,
  useNodesState,
  type Node,
  type Edge,
  type Connection,
  type NodeMouseHandler,
  type OnNodeDrag,
  type NodeTypes,
  type EdgeTypes,
} from "@xyflow/react";
import {
  Maximize,
  ZoomIn,
  ZoomOut,
  MousePointerClick,
  Plus,
  PencilLine,
  CircleCheck,
  TriangleAlert,
  Map as MapIcon,
  Clapperboard,
  GitBranch,
  MessageCircle,
  PauseCircle,
  X,
  SlidersHorizontal,
  Move,
  Link2,
  Scissors,
} from "lucide-react";
import "@xyflow/react/dist/style.css";

import { buildStoryGraph, type SceneInput } from "../../ls/build-graph.js";
import {
  buildEpisodeStructure,
  humanizeRouteCondition,
  type ChoiceOptionStructure,
  type ChoiceStructure,
  type EpisodeStructure,
  type GateRouteStructure,
  type GateStructure,
  type StateEffectStructure,
} from "../../ls/episode-structure.js";
import { blockLineRange, deleteLines, insertLine, type LineRange } from "../../ls/edit-source.js";
import { kindLabel, kindToken, parseLsBlocks, type LsBlock } from "../../ls/parse-blocks.js";
import { IconButton, Badge, Button, EmptyState, Spinner } from "../../ui/index.js";
import { useTr, type Entry } from "../../i18n/context.js";
import { AddBlockMenu, BlockFieldsEditor, describeBlock } from "../editor/EditorView.js";
import {
  INSERT_OPTIONS,
  minigamePresetInsertOptions,
  type InsertKind,
} from "../editor/block-templates.js";
import {
  CANVAS_BLOCK_MARKER,
  connectChoiceOptionToRoute,
  deleteBlockByLineWithResult,
  insertBlockAfterLineWithResult,
  insertBlockInsideOptionWithResult,
  insertCanvasBlockNearLineWithResult,
  moveLineRangeRelative,
  moveLineRangeIntoOptionWithResult,
  moveSiblingBlockByLine,
  splitCanvasBlockBeforeLineWithResult,
  type GateRouteConnection,
} from "../editor/card-ops.js";
import { layoutGraph } from "./layout.js";
import { StoryNode, type StoryNodeData } from "./StoryNode.js";
import { StoryEdge, type StoryEdgeData } from "./StoryEdge.js";
import {
  EpisodeFlowNode,
  type EpisodeFlowNodeData,
  type EpisodeFlowInsertOption,
  type EpisodeFlowStep,
  type EpisodeRouteQuickTarget,
  type EpisodeRouteResult,
} from "./EpisodeFlowNode.js";
import "./CanvasView.css";

const S = {
  zoomIn: { zh: "放大", en: "Zoom in" },
  zoomOut: { zh: "缩小", en: "Zoom out" },
  fitView: { zh: "适应视图", en: "Fit view" },

  emptyTitle: { zh: "还没有可呈现的剧情结构", en: "No story structure to show yet" },
  emptyBody: {
    zh: "为作品添加带有 @next / @choice / @gate 的剧集后，故事流程图会显示在这里。",
    en: "Add episodes with @next / @choice / @gate to your book, and the story flow will appear here.",
  },
  drillHint: { zh: "点“进入剧内”编辑这一集", en: "Use Edit inside to work inside an episode" },
  missingTargets: { zh: "{count} 个缺失的跳转目标", en: "{count} missing jump targets" },

  storyboardOf: { zh: "剧内编辑 · {title}", en: "Episode editor · {title}" },
  visualEditor: { zh: "可视化编辑", en: "Visual editor" },
  activeScene: { zh: "当前剧集", en: "Current episode" },
  addNode: { zh: "新建剧情块", en: "New story block" },
  addAfterSelected: { zh: "新块接在选中块后", en: "New block after selected" },
  addDockTitle: { zh: "插入剧情片段", en: "Insert story piece" },
  backToAddMenu: { zh: "返回添加菜单", en: "Back to add menu" },
  chooseMinigame: { zh: "选择小游戏模板", en: "Choose minigame template" },
  insertAfterBlock: { zh: "在这里加一段", en: "Add here" },
  splitHere: { zh: "从这里拆成块", en: "Split into a block here" },
  addOptionBranchContent: { zh: "给这个选项加剧情", en: "Add story to this option" },
  dropToInsert: { zh: "松开后插入到最近的主线位置", en: "Drop to insert near the closest main position" },
  zoomLevel: { zh: "缩放", en: "Zoom" },
  canvasMove: { zh: "双指移动", en: "Two-finger pan" },
  canvasConnect: { zh: "拖动连线改顺序", en: "Drag arrows to reorder" },
  canvasZoomGesture: { zh: "捏合或滑块缩放", en: "Pinch or slider zoom" },
  checkIdle: { zh: "待检查", en: "Not checked" },
  checkRunning: { zh: "检查中", en: "Checking" },
  checkValid: { zh: "已通过编译", en: "Compile passed" },
  checkInvalid: { zh: "需修复", en: "Needs fixes" },
  checkUnavailable: { zh: "编译不可用", en: "Compiler unavailable" },
  diagnosticsCount: { zh: "{count} 个问题需要修复", en: "{count} issues need fixes" },
  concurrent: { zh: "同时", en: "Together" },
  choiceNested: { zh: "请先选中主线节点", en: "Select a main-flow node first" },
  gateExists: { zh: "本集已有路由", en: "This episode already has a route" },
  modeStory: { zh: "剧集地图", en: "Story map" },
  modeEpisode: { zh: "剧内路线", en: "Episode routes" },
  modeStoryHint: { zh: "看全局路线和缺失跳转", en: "See route structure and missing jumps" },
  modeEpisodeHint: { zh: "看清选择分叉，并直接编辑剧情块", en: "See branches clearly and edit story blocks" },
  selectedBlock: { zh: "选中块", en: "Selected block" },
  closeInspector: { zh: "关闭编辑面板", en: "Close inspector" },
  loadingScene: { zh: "正在载入剧集…", en: "Loading episode…" },
  noSelectionTitle: { zh: "选择一个剧情块", en: "Select a story block" },
  noSelectionBody: {
    zh: "",
    en: "",
  },
  sourceRange: { zh: "第 {start}–{end} 行", en: "Lines {start}-{end}" },
  sourceLine: { zh: "第 {line} 行", en: "Line {line}" },
  stepCount: { zh: "{count} 个步骤", en: "{count} steps" },
  moreSteps: { zh: "还有 {count} 步", en: "{count} more steps" },
  narrativeBlock: { zh: "剧情块", en: "Story block" },
  choiceBlock: { zh: "心动选择", en: "Choice hub" },
  phoneBlock: { zh: "手机块", en: "Phone block" },
  routeBlock: { zh: "结局路线", en: "Route table" },
  pauseBlock: { zh: "停顿", en: "Pause" },
  logicBlock: { zh: "逻辑块", en: "Logic block" },
  eventBlock: { zh: "关系变化", en: "State change" },
  cgBlock: { zh: "CG 块", en: "CG block" },
  trickBlock: { zh: "体感块", en: "Trick block" },
  minigameBlock: { zh: "小游戏块", en: "Minigame block" },
  unknownBlock: { zh: "未识别块", en: "Unknown block" },
  directRoute: { zh: "出口影响", en: "Exit impact" },
  noDirectRoute: { zh: "不直接跳转", en: "Does not directly jump" },
  stateDrivenRoute: { zh: "通过状态影响路线", en: "State affects route" },
  stateChangeOnly: { zh: "改变状态，未匹配出口", en: "State changed; no matched exit" },
  possibleRoute: { zh: "可能影响", en: "May affect" },
  success: { zh: "成功", en: "Success" },
  fail: { zh: "失败", en: "Fail" },
  any: { zh: "任意", en: "Any" },
  direct: { zh: "直接", en: "Direct" },
  safe: { zh: "稳妥", en: "Safe" },
  brave: { zh: "勇敢", en: "Brave" },
  check: { zh: "检定", en: "Check" },
  effects: { zh: "影响", en: "Effects" },
  fallback: { zh: "兜底", en: "Fallback" },
  fallbackRoute: { zh: "未命中走兜底", en: "Falls through" },
  condition: { zh: "条件", en: "Condition" },
  routeTo: { zh: "去往", en: "Route" },
  ending: { zh: "结局", en: "Ending" },
  quickConnect: { zh: "接剧情", en: "Route to" },
  quickConnectTitle: { zh: "设置这个选项会通向哪里", en: "Set where this option routes" },
  routeCandidate: { zh: "可连接目标", en: "Connect target" },
  routeCandidateSummary: { zh: "拖选项线接到这里", en: "Drag an option line here" },
  routeOverview: { zh: "路线总览", en: "Route overview" },
  ifRoute: { zh: "如果", en: "If" },
  elseIfRoute: { zh: "否则若", en: "Else if" },
  optionBranch: { zh: "选项后剧情", en: "After option" },
  emptyOptionBranch: { zh: "把这个选择造成的剧情接在这里", en: "Connect the story caused by this choice here" },
  moveIntoOption: { zh: "拖线可把剧情块接到这个选项后", en: "Connect a story block to place it after this option" },
  moreEffects: { zh: "+{count} 个影响", en: "+{count} effects" },
  moreRoutes: { zh: "+{count} 条路线", en: "+{count} routes" },
  routeEpisodes: { zh: "已有剧集", en: "Episodes" },
  routeEndings: { zh: "结局", en: "Endings" },
  missingTarget: { zh: "缺失剧集", en: "Missing episode" },
  createMissing: { zh: "创建剧集", en: "Create episode" },
  dragStepToReorder: { zh: "拖动这一步重新排序", en: "Drag this step to reorder" },
} satisfies Record<string, Entry>;

/** One input scene: a scene id and its raw `.ls` source. */
export interface CanvasScene {
  readonly id: string;
  readonly source: string;
}

export interface CanvasValidationState {
  readonly status: "idle" | "checking" | "valid" | "invalid" | "unavailable";
  readonly diagnostics?: readonly string[];
}

type CanvasMode = "story" | "episode";

export interface CanvasViewProps {
  /** The work's scenes — the structural source of truth for the graph. */
  readonly scenes: CanvasScene[];
  /** Id of the currently-selected scene (highlighted on the canvas). */
  readonly activeId?: string;
  /** Fired when a scene node is clicked. Only fires for real scene nodes. */
  readonly onSelect?: (id: string) => void;
  /** The currently-selected scene source; when provided the canvas becomes editable. */
  readonly activeSource?: string;
  /** Scene id that owns activeSource. Null means the active source is still loading. */
  readonly activeSourceId?: string | null;
  /** Update the selected scene source from the canvas-side card inspector. */
  readonly onActiveSourceChange?: (source: string) => void;
  /** Insert a node template near the selected visual/source node. */
  readonly onInsertBlock?: (kind: InsertKind, afterLine?: number | null) => void;
  /** Undo the latest source mutation initiated from the canvas. */
  readonly onUndoSourceChange?: () => void;
  /** Whether the canvas source undo stack has an entry. */
  readonly canUndoSourceChange?: boolean;
  /** Create a scene for a dangling @next target. */
  readonly onCreateScene?: (branchKey: string) => void;
  /** Compile validation status for the selected scene after canvas edits. */
  readonly validation?: CanvasValidationState;
  /** Currently selected source line in the canvas editor. */
  readonly selectedLine?: number | null;
  /** Fired when a visual/source node becomes selected. */
  readonly onSelectedLineChange?: (line: number | null) => void;
  /** Internal/test seam for rendering a specific visual layer first. */
  readonly initialMode?: CanvasMode;
}

const storyNodeTypes: NodeTypes = { story: StoryNode };
const storyEdgeTypes: EdgeTypes = { story: StoryEdge };
const episodeNodeTypes: NodeTypes = { episode: EpisodeFlowNode };
const episodeEdgeTypes: EdgeTypes = { story: StoryEdge };

const PALETTE_GROUPS: readonly {
  readonly label: Entry;
  readonly kinds: readonly InsertKind[];
}[] = [
  {
    label: { zh: "写作", en: "Writing" },
    kinds: ["dialogue", "narration", "you", "choice"],
  },
  {
    label: { zh: "画面音频", en: "Staging" },
    kinds: ["bg", "char", "music", "sfx", "cg", "phone", "pause"],
  },
  {
    label: { zh: "互动状态", en: "Interaction" },
    kinds: ["affection", "signal", "butterfly", "trick", "minigame", "achievement", "gate"],
  },
];

const QUICK_INSERT_KINDS: readonly InsertKind[] = ["dialogue", "bg", "choice", "phone"];
const INSERT_OPTIONS_BY_KIND = new Map(INSERT_OPTIONS.map((opt) => [opt.kind, opt]));
const INSERT_DRAG_MIME = "application/x-lunaria-insert-kind";
const FLOW_MAIN_X = 0;
const FLOW_BRANCH_GAP = 440;
const FLOW_MAIN_NODE_WIDTH = 320;
const FLOW_OPTION_NODE_WIDTH = 260;
const FLOW_BRANCH_NODE_WIDTH = 292;
const FLOW_TARGET_NODE_WIDTH = 230;
const FLOW_BLOCK_BASE_HEIGHT = 178;
const FLOW_OPTION_TOP = 218;
const FLOW_OPTION_BRANCH_TOP = 138;
const FLOW_ROUTE_TARGET_TOP = 318;
const FLOW_MAIN_GAP = 132;
const FLOW_MAIN_LINE_X = FLOW_MAIN_X;
const FLOW_INSERT_LINE_SIDE_GAP = 12;
const FLOW_INSERT_WIDTH = 26;
const FLOW_TOP_PADDING = 132;
const REORDER_SNAP_DISTANCE = 156;
const FLOW_ARROW_MARKER_SIZE = 12;

/** Coral marker for ending edges, neutral elsewhere — kept token-aligned. */
function markerColorFor(kind: StoryEdgeData["kind"]): string {
  switch (kind) {
    case "end":
      return "var(--kind-ending)";
    case "choice":
      return "var(--accent-rose)";
    case "gate":
      return "var(--kind-gate)";
    case "dangling":
      return "var(--accent-danger)";
    case "next":
      return "var(--text-secondary)";
    default:
      return "var(--border-strong)";
  }
}

// ── Level 1: story-structure graph ────────────────────────────────────────────

function StoryLevel({
  scenes,
  activeId,
  onSelect,
  onDrill,
  canDrill,
  onCreateMissing,
}: {
  scenes: CanvasScene[];
  activeId?: string;
  onSelect?: (id: string) => void;
  onDrill?: (id: string) => void;
  canDrill: boolean;
  onCreateMissing?: (branchKey: string) => void;
}) {
  const tr = useTr();
  const { fitView } = useReactFlow();

  const { nodes, edges, missingCount } = useMemo(() => {
    const input: SceneInput[] = scenes.map((s) => ({ id: s.id, source: s.source }));
    const graph = buildStoryGraph(input);
    const positions = layoutGraph(graph);

    const flowNodes: Node<StoryNodeData>[] = graph.nodes.map((n) => ({
      id: n.id,
      type: "story",
      position: positions.get(n.id) ?? { x: 0, y: 0 },
      selectable: n.kind === "scene",
      data: {
        label: n.label,
        kind: n.kind,
        sceneId: n.kind === "missing" ? n.label : (n.sceneId ?? n.id),
        branchKey: n.branchKey ?? n.id,
        missingTarget: n.kind === "missing" ? n.label : undefined,
        onCreateMissing: n.kind === "missing" ? onCreateMissing : undefined,
        canDrill: n.kind === "scene" && canDrill,
        onDrill: n.kind === "scene" && canDrill ? onDrill : undefined,
        active: n.kind === "scene" && (n.sceneId === activeId || n.id === activeId),
        endingType: n.endingType,
        missing: n.missing,
      },
    }));

    const flowEdges: Edge<StoryEdgeData>[] = graph.edges.map((e) => {
      const kind = e.kind ?? "next";
      return {
        id: e.id,
        source: e.source,
        target: e.target,
        type: "story",
        label: e.label,
        data: { kind },
        markerEnd: { type: MarkerType.ArrowClosed, width: FLOW_ARROW_MARKER_SIZE, height: FLOW_ARROW_MARKER_SIZE, color: markerColorFor(kind) },
      };
    });

    const missing = graph.nodes.filter((n) => n.kind === "missing").length;
    return { nodes: flowNodes, edges: flowEdges, missingCount: missing };
  }, [scenes, activeId, canDrill, onCreateMissing, onDrill]);

  // Manual double-click detection on the reliable onNodeClick: a second click
  // on the same scene within 350ms drills into its step graph; a single click
  // selects. (react-flow's own onNodeDoubleClick proved unreliable here, and
  // this keeps the gesture framework-agnostic.)
  const lastClick = useRef<{ id: string; t: number }>({ id: "", t: 0 });
  const handleNodeClick = useCallback<NodeMouseHandler>(
    (_e, node) => {
      const data = node.data as StoryNodeData;
      if (data.kind !== "scene") return;
      const now = Date.now();
      const prev = lastClick.current;
      if (prev.id === data.sceneId && now - prev.t < 350) {
        lastClick.current = { id: "", t: 0 };
        onDrill?.(data.sceneId);
      } else {
        lastClick.current = { id: data.sceneId, t: now };
        onSelect?.(data.sceneId);
      }
    },
    [onSelect, onDrill],
  );

  if (nodes.length === 0) {
    return (
      <div className="lc-canvas lc-canvas--empty">
        <EmptyState
          icon={<Maximize size={20} />}
          title={tr(S.emptyTitle)}
          description={tr(S.emptyBody)}
        />
      </div>
    );
  }

  return (
    <div className="lc-canvas">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={storyNodeTypes}
        edgeTypes={storyEdgeTypes}
        onNodeClick={handleNodeClick}
        zoomOnDoubleClick={false}
        panOnScroll
        panOnScrollMode={PanOnScrollMode.Free}
        panOnScrollSpeed={0.7}
        paneClickDistance={4}
        nodeClickDistance={4}
        zoomActivationKeyCode={["Meta", "Control"]}
        zoomOnScroll={false}
        zoomOnPinch
        fitView
        fitViewOptions={{ padding: 0.24, maxZoom: 1 }}
        minZoom={0.2}
        maxZoom={1.75}
        nodesDraggable={false}
        nodesConnectable={false}
        deleteKeyCode={null}
        elementsSelectable
        proOptions={{ hideAttribution: true }}
        className="lc-canvas__flow"
      >
        <Background variant={BackgroundVariant.Dots} gap={26} size={1.5} color="var(--border-subtle)" />
        <Panel position="top-right" className="lc-canvas__toolbar">
          <FlowZoomControls onFit={() => fitView({ padding: 0.24, duration: 240, maxZoom: 1 })} />
        </Panel>

        {canDrill ? (
          <Panel position="top-left" className="lc-canvas__hint">
            <Badge variant="tonal" tone="neutral" size="md">
              <MousePointerClick size={13} aria-hidden="true" /> {tr(S.drillHint)}
            </Badge>
          </Panel>
        ) : null}

        {missingCount > 0 ? (
          <Panel position="bottom-left" className="lc-canvas__warning">
            <Badge variant="tonal" tone="danger" size="md" dot>
              {tr(S.missingTargets, { count: missingCount })}
            </Badge>
          </Panel>
        ) : null}
      </ReactFlow>
    </div>
  );
}

// ── Level 2: grouped episode blocks ──────────────────────────────────────────

type EpisodeVisualKind =
  | "narrative"
  | "choice"
  | "phone"
  | "route"
  | "pause"
  | "logic"
  | "event"
  | "cg"
  | "trick"
  | "minigame"
  | "unknown";

interface EpisodeVisualBlock {
  readonly id: string;
  readonly kind: EpisodeVisualKind;
  readonly title: string;
  readonly summary: string;
  readonly line: number;
  readonly endLine: number;
  readonly blocks: readonly LsBlock[];
  readonly choice?: ChoiceStructure;
  readonly gate?: GateStructure;
}

const COMPOUND_BLOCK_KINDS = new Set<LsBlock["kind"]>(["choice", "phone", "gate", "achievement", "check", "if"]);
const SINGLE_BLOCK_KINDS = new Set<LsBlock["kind"]>([
  "cg",
  "pause",
  "trick",
  "minigame",
  "unknown",
]);

function clipText(value: string, max = 54): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function visualKindFor(block: LsBlock): EpisodeVisualKind {
  switch (block.kind) {
    case "choice":
    case "option":
    case "check":
      return "choice";
    case "phone":
    case "phoneText":
      return "phone";
    case "gate":
      return "route";
    case "pause":
      return "pause";
    case "cg":
      return "cg";
    case "trick":
      return "trick";
    case "minigame":
      return "minigame";
    case "if":
      return "logic";
    case "achievement":
    case "affection":
    case "signal":
    case "butterfly":
      return "event";
    case "unknown":
      return "unknown";
    default:
      return "narrative";
  }
}

function visualKindLabel(kind: EpisodeVisualKind): Entry {
  switch (kind) {
    case "choice":
      return S.choiceBlock;
    case "phone":
      return S.phoneBlock;
    case "route":
      return S.routeBlock;
    case "pause":
      return S.pauseBlock;
    case "logic":
      return S.logicBlock;
    case "event":
      return S.eventBlock;
    case "cg":
      return S.cgBlock;
    case "trick":
      return S.trickBlock;
    case "minigame":
      return S.minigameBlock;
    case "unknown":
      return S.unknownBlock;
    default:
      return S.narrativeBlock;
  }
}

function iconForVisualKind(kind: EpisodeVisualKind) {
  switch (kind) {
    case "choice":
      return <GitBranch size={16} aria-hidden />;
    case "phone":
      return <MessageCircle size={16} aria-hidden />;
    case "route":
      return <MapIcon size={16} aria-hidden />;
    case "pause":
      return <PauseCircle size={16} aria-hidden />;
    case "cg":
      return <Clapperboard size={16} aria-hidden />;
    case "trick":
    case "minigame":
      return <MousePointerClick size={16} aria-hidden />;
    default:
      return <Clapperboard size={16} aria-hidden />;
  }
}

function visualToneFor(kind: EpisodeVisualKind): string {
  switch (kind) {
    case "choice":
      return "var(--kind-choice)";
    case "phone":
      return "var(--kind-phone)";
    case "route":
      return "var(--kind-gate)";
    case "pause":
      return "var(--kind-pause)";
    case "logic":
      return "var(--kind-if)";
    case "cg":
      return "var(--kind-cg)";
    case "trick":
      return "var(--kind-trick)";
    case "minigame":
      return "var(--kind-minigame)";
    case "event":
      return "var(--kind-affection)";
    case "unknown":
      return "var(--kind-label)";
    default:
      return "var(--kind-dialogue)";
  }
}

function outcomeLabel(route: GateRouteStructure): string {
  return route.outcome.kind === "next" ? route.outcome.value : `Ending · ${route.outcome.value}`;
}

function routeConditionText(
  route: GateRouteStructure,
  choices: readonly ChoiceStructure[],
  fallbackLabel = "Fallback",
  directLabel = "Direct",
): string {
  if (route.isFallback) return fallbackLabel;
  return humanizeRouteCondition(route, choices) || route.condition || directLabel;
}

function effectDisplayLabel(effect: StateEffectStructure, tr: ReturnType<typeof useTr>): string {
  if (effect.scope === "success") return `${tr(S.success)}${tr(S.effects)} ${effect.label}`;
  if (effect.scope === "fail") return `${tr(S.fail)}${tr(S.effects)} ${effect.label}`;
  return `${tr(S.effects)} ${effect.label}`;
}

function fallbackRouteForGate(gate?: GateStructure): GateRouteStructure | undefined {
  if (!gate) return undefined;
  return gate.routes.find((route) => route.isFallback) ?? (gate.routes.length === 1 && !gate.routes[0]?.condition ? gate.routes[0] : undefined);
}

function optionModeLabel(option: ChoiceOptionStructure): Entry | null {
  if (option.mode === "brave") return S.brave;
  if (option.mode === "safe") return S.safe;
  return null;
}

function checkLabel(option: ChoiceOptionStructure): string | null {
  if (!option.check) return null;
  const attr = option.check.attr ?? "?";
  const dc = option.check.dc ?? "?";
  return `${attr} ≥ ${dc}`;
}

function titleForVisualBlock(
  kind: EpisodeVisualKind,
  blocks: readonly LsBlock[],
  choice?: ChoiceStructure,
  gate?: GateStructure,
): string {
  const first = blocks[0];
  if (!first) return "";
  if (kind === "narrative") {
    const bg = blocks.find((b) => b.kind === "bg");
    if (bg?.kind === "bg" && bg.name) return `Scene · ${bg.name}`;
    const line = blocks.find((b) => b.kind === "dialogue" || b.kind === "narration");
    if (line) return clipText(describeBlock(line), 32);
    return "Story passage";
  }
  if (kind === "choice") {
    const count = choice?.options.length ?? blocks.filter((b) => b.kind === "option").length;
    return count > 0 ? `Choice · ${count} options` : "Choice branches";
  }
  if (kind === "phone") return "Phone messages";
  if (kind === "route") return gate?.routes.length ? `Routes · ${gate.routes.length}` : "Routes";
  if (kind === "pause") return "Pause";
  if (kind === "cg") return clipText(describeBlock(first), 32);
  if (kind === "trick") return clipText(describeBlock(first), 32);
  if (kind === "minigame") return clipText(describeBlock(first), 32);
  if (kind === "logic") return clipText(describeBlock(first), 32);
  if (kind === "event") return kindLabel(first.kind);
  return "Unrecognized content";
}

function summaryForVisualBlock(
  blocks: readonly LsBlock[],
  choice?: ChoiceStructure,
  gate?: GateStructure,
): string {
  if (choice) {
    return choice.options
      .slice(0, 3)
      .map((option) => option.text || option.id || "Untitled option")
      .join(" / ");
  }
  if (gate) {
    return `Evaluated in order · ${gate.routes.length} routes${gate.hasFallback ? " · fallback set" : " · no fallback"}`;
  }
  return blocks
    .filter((b) => b.kind !== "episode")
    .slice(0, 3)
    .map((b) => clipText(describeBlock(b), 34))
    .join(" / ");
}

function makeVisualBlock(
  kind: EpisodeVisualKind,
  blocks: readonly LsBlock[],
  index: number,
  choice?: ChoiceStructure,
  gate?: GateStructure,
): EpisodeVisualBlock {
  const line = blocks[0]?.line ?? index;
  const endLine = blocks[blocks.length - 1]?.line ?? line;
  return {
    id: `${kind}:${line}:${endLine}:${index}`,
    kind,
    title: titleForVisualBlock(kind, blocks, choice, gate),
    summary: summaryForVisualBlock(blocks, choice, gate),
    line,
    endLine,
    blocks,
    ...(choice ? { choice } : {}),
    ...(gate ? { gate } : {}),
  };
}

function canvasBoundaryLines(source?: string): ReadonlySet<number> {
  if (!source) return new Set<number>();
  const result = new Set<number>();
  source.split(/\r?\n/).forEach((line, index) => {
    if (line.trim() === CANVAS_BLOCK_MARKER) result.add(index + 1);
  });
  return result;
}

function hasCanvasBoundaryBetween(boundaries: ReadonlySet<number>, fromLine: number, toLine: number): boolean {
  for (let line = fromLine + 1; line < toLine; line++) {
    if (boundaries.has(line)) return true;
  }
  return false;
}

function concurrentGroupAt(blocks: readonly LsBlock[], index: number, boundaries: ReadonlySet<number>): LsBlock[] | null {
  const anchor = blocks[index];
  if (!anchor || anchor.concurrent) return null;
  const group = [anchor];
  let previousLine = anchor.line;
  for (let j = index + 1; j < blocks.length; j++) {
    const follower = blocks[j]!;
    if (!follower.concurrent) break;
    if (follower.depth !== anchor.depth) break;
    if (hasCanvasBoundaryBetween(boundaries, previousLine, follower.line)) break;
    group.push(follower);
    previousLine = follower.line;
  }
  return group.length > 1 ? group : null;
}

function buildEpisodeVisualBlocks(blocks: readonly LsBlock[], structure?: EpisodeStructure, source?: string): EpisodeVisualBlock[] {
  const result: EpisodeVisualBlock[] = [];
  let pending: LsBlock[] = [];
  const boundaries = canvasBoundaryLines(source);
  const flushPending = () => {
    if (pending.length === 0) return;
    result.push(makeVisualBlock("narrative", pending, result.length));
    pending = [];
  };

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]!;
    if (block.kind === "episode") continue;
    const previous = pending[pending.length - 1];
    if (previous && hasCanvasBoundaryBetween(boundaries, previous.line, block.line)) {
      flushPending();
    }
    const concurrentGroup = concurrentGroupAt(blocks, i, boundaries);
    if (concurrentGroup) {
      flushPending();
      result.push(makeVisualBlock(visualKindFor(block), concurrentGroup, result.length));
      i += concurrentGroup.length - 1;
      continue;
    }
    if (block.kind === "bg") {
      flushPending();
      pending.push(block);
      continue;
    }
    if (COMPOUND_BLOCK_KINDS.has(block.kind)) {
      flushPending();
      const group: LsBlock[] = [block];
      let j = i + 1;
      while (j < blocks.length && blocks[j]!.depth > block.depth) {
        group.push(blocks[j]!);
        j++;
      }
      const choice = block.kind === "choice" ? structure?.choices.find((item) => item.line === block.line) : undefined;
      const gate = block.kind === "gate" ? structure?.gate : undefined;
      result.push(makeVisualBlock(visualKindFor(block), group, result.length, choice, gate));
      i = j - 1;
      continue;
    }
    if (SINGLE_BLOCK_KINDS.has(block.kind)) {
      flushPending();
      result.push(makeVisualBlock(visualKindFor(block), [block], result.length));
      continue;
    }
    pending.push(block);
  }
  flushPending();
  return result;
}

function lineInVisualBlock(line: number | null | undefined, block: EpisodeVisualBlock): boolean {
  return typeof line === "number" && line >= block.line && line <= block.endLine;
}

function stepsForFlowNode(block: EpisodeVisualBlock): EpisodeFlowStep[] {
  if (block.choice || block.gate) return [];
  return block.blocks.slice(0, 4).map((step) => ({
    kind: kindLabel(step.kind),
    text: describeBlock(step),
    tone: `var(${kindToken(step.kind)})`,
    concurrent: step.concurrent,
  }));
}

function blocksInsideRange(blocks: readonly LsBlock[], range: LineRange): LsBlock[] {
  return blocks.filter((block) => block.line > range.start && block.line < range.end);
}

function optionBodySteps(blocks: readonly LsBlock[], option: ChoiceOptionStructure): EpisodeFlowStep[] {
  return blocksInsideRange(blocks, option.range)
    .filter((block) => block.kind !== "param")
    .slice(0, 4)
    .map((step) => ({
      kind: kindLabel(step.kind),
      text: describeBlock(step),
      tone: `var(${kindToken(step.kind)})`,
      concurrent: step.concurrent,
    }));
}

function groupConcurrentBlocks(blocks: readonly LsBlock[]): readonly (LsBlock | readonly LsBlock[])[] {
  const groups: (LsBlock | LsBlock[])[] = [];

  for (const block of blocks) {
    if (block.concurrent) {
      const previous = groups[groups.length - 1];
      if (Array.isArray(previous)) {
        previous.push(block);
      } else if (previous) {
        groups[groups.length - 1] = [previous, block];
      } else {
        groups.push([block]);
      }
      continue;
    }
    groups.push(block);
  }
  return groups;
}

function isConcurrentBlockGroup(item: LsBlock | readonly LsBlock[]): item is readonly LsBlock[] {
  return Array.isArray(item);
}

function optionBadges(option: ChoiceOptionStructure, tr: ReturnType<typeof useTr>): string[] {
  const badges: string[] = [];
  const mode = optionModeLabel(option);
  if (mode) badges.push(tr(mode));
  const check = checkLabel(option);
  if (check) badges.push(`${tr(S.check)} ${check}`);
  return badges;
}

function optionImpactLabels(
  option: ChoiceOptionStructure,
  tr: ReturnType<typeof useTr>,
): string[] {
  const visible = option.effects.slice(0, 2).map((effect) => clipText(effectDisplayLabel(effect, tr), 24));
  const hidden = option.effects.length - visible.length;
  return hidden > 0 ? [...visible, tr(S.moreEffects, { count: hidden })] : visible;
}

function targetToneForRoute(route: GateRouteStructure): string {
  if (route.outcome.kind === "end") return "var(--kind-ending)";
  if (route.isFallback) return "var(--kind-fallback)";
  return "var(--kind-route-target)";
}

function flowNodeTone(kind: EpisodeVisualKind | EpisodeFlowNodeData["nodeKind"]): string {
  if (kind === "option") return "var(--kind-option)";
  if (kind === "target") return "var(--kind-ending)";
  return visualToneFor(kind as EpisodeVisualKind);
}

const DEFAULT_ENDING_TARGETS = ["to_be_continued", "complete", "bad_ending"] as const;

function routeTargetIndexForScenes(
  scenes: readonly CanvasScene[],
  tr: ReturnType<typeof useTr>,
  excludeTargetBranchKey?: string | null,
): {
  readonly knownBranchKeys: ReadonlySet<string>;
  readonly routeTargets: readonly EpisodeRouteQuickTarget[];
} {
  const knownBranchKeys = new Set<string>();
  const routeTargets: EpisodeRouteQuickTarget[] = [];

  for (const scene of scenes) {
    const structure = buildEpisodeStructure(scene.source);
    const branchKey = structure.meta.branchKey || scene.id;
    if (!branchKey || knownBranchKeys.has(branchKey)) continue;
    knownBranchKeys.add(branchKey);
    if (excludeTargetBranchKey && branchKey === excludeTargetBranchKey) continue;
    routeTargets.push({
      kind: "next",
      value: branchKey,
      label: structure.meta.title ? `${structure.meta.title} · ${branchKey}` : branchKey,
      group: "episode",
    });
  }

  for (const value of DEFAULT_ENDING_TARGETS) {
    routeTargets.push({
      kind: "end",
      value,
      label: `${tr(S.ending)} · ${value}`,
      group: "ending",
    });
  }

  return { knownBranchKeys, routeTargets };
}

function targetNodeRouteData(
  route: GateRouteStructure,
  knownBranchKeys: ReadonlySet<string>,
  tr: ReturnType<typeof useTr>,
  onCreateScene?: (branchKey: string) => void,
): Pick<
  EpisodeFlowNodeData,
  "summary" | "missing" | "missingLabel" | "createMissingLabel" | "missingTarget" | "onCreateMissing"
> {
  if (route.outcome.kind === "end") {
    return { summary: tr(S.ending) };
  }

  const missing = !knownBranchKeys.has(route.outcome.value);
  if (!missing) {
    return { summary: tr(S.routeTo) };
  }

  return {
    summary: tr(S.missingTarget),
    missing: true,
    missingLabel: tr(S.missingTarget),
    createMissingLabel: tr(S.createMissing),
    missingTarget: route.outcome.value,
    onCreateMissing: onCreateScene,
  };
}

function EpisodeLoadingBoard() {
  const tr = useTr();
  return (
    <div className="lc-episode-board lc-episode-board--empty" role="status" aria-live="polite">
      <span className="lc-episode-board__loading">
        <Spinner size="sm" />
        {tr(S.loadingScene)}
      </span>
    </div>
  );
}

function EpisodeFlowSemanticSummary({
  nodes,
  edges,
}: {
  nodes: readonly Node<EpisodeFlowNodeData>[];
  edges: readonly Edge<StoryEdgeData>[];
}) {
  const tr = useTr();
  return (
    <div className="lc-episode-flow__semantic">
      {nodes.filter((node) => node.data.nodeKind !== "lane").map((node) => {
        const data = node.data;
        return (
          <div
            key={node.id}
            className="lc-episode-flow__semantic-node"
            data-node-kind={data.nodeKind}
            data-sequence-connectable={data.sequenceConnectable === true || undefined}
            data-source-connectable={data.sourceConnectable === true || undefined}
            data-target-connectable={data.targetConnectable === true || undefined}
            aria-keyshortcuts={data.onDeleteLine ? "Delete Backspace" : undefined}
          >
            <span>{data.label}</span>
            <span>{data.title}</span>
            {data.range ? <span>{data.range}</span> : null}
            {data.summary ? <span>{data.summary}</span> : null}
            {data.summary ? <span>{data.summary} {data.title}</span> : null}
            {data.badges?.map((badge) => <span key={badge}>{badge}</span>)}
            {data.steps?.map((step) => (
              <span key={`${step.kind}:${step.text}`}>
                {step.kind} {step.concurrent ? "Together " : ""}{step.text}
              </span>
            ))}
            {data.impacts?.map((impact) => <span key={impact}>{impact}</span>)}
            {data.quickConnectLabel ? <span>{data.quickConnectLabel}</span> : null}
            {data.routeTargets?.map((target) => <span key={`${target.kind}:${target.value}`}>{target.label}</span>)}
            {data.routeOutcomeKind === "next" && typeof data.routeOutcomeValue === "string" ? (
              <span>{tr(S.routeTo)} {data.routeOutcomeValue}</span>
            ) : null}
            {data.nodeKind === "target" && data.label && typeof data.routeOutcomeValue === "string" ? (
              <span>
                {data.label} → {data.routeOutcomeKind === "end" ? `${tr(S.ending)} · ${data.routeOutcomeValue}` : data.routeOutcomeValue}
              </span>
            ) : null}
            {data.missingLabel ? <span>{data.missingLabel}</span> : null}
            {data.createMissingLabel ? <span>{data.createMissingLabel}</span> : null}
            {data.muted ? <span>{data.muted}</span> : null}
          </div>
        );
      })}
      {edges.map((edge) => (
        edge.label ? <span key={edge.id} className="lc-episode-flow__semantic-edge">{edge.label}</span> : null
      ))}
    </div>
  );
}

function FlowZoomControls({
  onFit,
}: {
  onFit: () => void;
}) {
  const tr = useTr();
  const viewport = useViewport();
  const { setViewport, zoomIn, zoomOut } = useReactFlow();
  const zoomPercent = Math.round(viewport.zoom * 100);

  return (
    <div className="lc-canvas-zoom" aria-label={tr(S.zoomLevel)}>
      <IconButton label={tr(S.zoomOut)} size="sm" onClick={() => zoomOut({ duration: 180 })}>
        <ZoomOut size={16} />
      </IconButton>
      <label className="lc-canvas-zoom__slider">
        <span className="lc-canvas-zoom__label">
          <SlidersHorizontal size={14} aria-hidden="true" />
          {zoomPercent}%
        </span>
        <input
          aria-label={tr(S.zoomLevel)}
          type="range"
          min="35"
          max="150"
          step="5"
          value={Math.max(35, Math.min(150, zoomPercent))}
          onChange={(event) => {
            const nextZoom = Number(event.target.value) / 100;
            setViewport({ x: viewport.x, y: viewport.y, zoom: nextZoom }, { duration: 80 });
          }}
        />
      </label>
      <IconButton label={tr(S.zoomIn)} size="sm" onClick={() => zoomIn({ duration: 180 })}>
        <ZoomIn size={16} />
      </IconButton>
      <IconButton label={tr(S.fitView)} size="sm" onClick={onFit}>
        <Maximize size={16} />
      </IconButton>
    </div>
  );
}

function estimateChoiceOptionNodeHeight(option: ChoiceOptionStructure): number {
  const titleRows = Math.min(3, Math.ceil(Math.max(1, option.text?.length ?? 1) / 18));
  const badgeRows = optionModeLabel(option) || option.check ? 1 : 0;
  const impactRows = Math.min(2, option.effects.length) + (option.effects.length > 2 ? 1 : 0);
  return 126 + titleRows * 21 + badgeRows * 30 + impactRows * 29;
}

function estimateOptionBranchNodeHeight(blocks: readonly LsBlock[], option: ChoiceOptionStructure): number {
  const branchStepCount = blocksInsideRange(blocks, option.range)
    .filter((item) => item.kind !== "check" && item.kind !== "param")
    .slice(0, 4).length;
  return 96 + Math.max(1, branchStepCount) * 34;
}

function optionBranchTopForChoice(choice: ChoiceStructure): number {
  const optionHeight = Math.max(112, ...choice.options.map(estimateChoiceOptionNodeHeight));
  return optionHeight + 156;
}

function estimateChoiceFlowHeight(block: EpisodeVisualBlock): number {
  const choice = block.choice;
  if (!choice) return FLOW_OPTION_TOP + FLOW_OPTION_BRANCH_TOP + 168 + FLOW_MAIN_GAP;
  const branchTop = optionBranchTopForChoice(choice);
  const branchHeight = Math.max(
    132,
    ...choice.options.map((option) => estimateOptionBranchNodeHeight(block.blocks, option)),
  );
  return FLOW_OPTION_TOP + branchTop + branchHeight + FLOW_MAIN_GAP;
}

function estimateFlowNodeHeight(
  block: EpisodeVisualBlock,
  _fallbackRoute?: GateRouteStructure,
): number {
  if (block.kind === "choice") {
    return estimateChoiceFlowHeight(block);
  }
  if (block.kind === "route") {
    return FLOW_ROUTE_TARGET_TOP + 96 + FLOW_MAIN_GAP;
  }
  return FLOW_BLOCK_BASE_HEIGHT + Math.min(4, Math.max(0, block.blocks.length - 1)) * 34 + FLOW_MAIN_GAP;
}

function approxFlowNodeSize(node: Node<EpisodeFlowNodeData>): { width: number; height: number } {
  switch (node.data.nodeKind) {
    case "choice":
      return { width: 340, height: 138 };
    case "option":
      return { width: FLOW_OPTION_NODE_WIDTH, height: 172 };
    case "branch":
      return { width: FLOW_BRANCH_NODE_WIDTH, height: 138 };
    case "target":
      return { width: FLOW_TARGET_NODE_WIDTH, height: 96 };
    case "route":
      return { width: 272, height: node.data.sidecar ? 180 : 132 };
    case "insert":
      return { width: FLOW_INSERT_WIDTH, height: FLOW_INSERT_WIDTH };
    default:
      return { width: FLOW_MAIN_NODE_WIDTH, height: 154 };
  }
}

function anchorLineFromNode(node: Node<EpisodeFlowNodeData>): number | null {
  if (!node.id.startsWith("block:") && !node.id.startsWith("insert:")) return null;
  const line = node.data.line;
  return typeof line === "number" ? line : null;
}

function insertAnchorLineForVisualBlock(block: EpisodeVisualBlock): number {
  if (block.choice || block.gate || block.kind === "phone") return block.line;
  return block.endLine;
}

function nearestAnchorLine(nodes: readonly Node<EpisodeFlowNodeData>[], point: { x: number; y: number }): number | null {
  let best: { line: number; distance: number } | null = null;
  for (const node of nodes) {
    const line = anchorLineFromNode(node);
    if (line === null) continue;
    const size = approxFlowNodeSize(node);
    const dx = (node.position.x + size.width / 2) - point.x;
    const dy = (node.position.y + size.height / 2) - point.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (!best || distance < best.distance) best = { line, distance };
  }
  return best?.line ?? null;
}

function visualBlockIdFromNodeId(nodeId: string): string | null {
  return nodeId.startsWith("block:") ? nodeId.slice("block:".length) : null;
}

function canReorderVisualBlock(block: EpisodeVisualBlock): boolean {
  return block.kind !== "route" && !block.gate;
}

function canMoveVisualBlockIntoOption(block: EpisodeVisualBlock): boolean {
  return canReorderVisualBlock(block) && !block.choice;
}

function nodeCenterY(node: Node<EpisodeFlowNodeData>): number {
  return node.position.y + approxFlowNodeSize(node).height / 2;
}

function nearestReorderTarget(
  nodes: readonly Node<EpisodeFlowNodeData>[],
  visualBlocks: readonly EpisodeVisualBlock[],
  draggedNode: Node<EpisodeFlowNodeData>,
): { target: EpisodeVisualBlock; placement: "before" | "after" } | null {
  const draggedId = visualBlockIdFromNodeId(draggedNode.id);
  if (!draggedId) return null;
  const byId = new Map(visualBlocks.map((block) => [block.id, block]));
  const draggedBlock = byId.get(draggedId);
  if (!draggedBlock || !canReorderVisualBlock(draggedBlock)) return null;

  const draggedCenter = nodeCenterY(draggedNode);
  let best: { block: EpisodeVisualBlock; center: number; distance: number } | null = null;

  for (const node of nodes) {
    const id = visualBlockIdFromNodeId(node.id);
    if (!id || id === draggedId) continue;
    const block = byId.get(id);
    if (!block || !canReorderVisualBlock(block)) continue;
    const center = nodeCenterY(node);
    const distance = Math.abs(center - draggedCenter);
    if (!best || distance < best.distance) best = { block, center, distance };
  }

  if (!best) return null;
  if (best.distance > REORDER_SNAP_DISTANCE) return null;
  return {
    target: best.block,
    placement: draggedCenter >= best.center ? "after" : "before",
  };
}

function nearestBranchDropTarget(
  nodes: readonly Node<EpisodeFlowNodeData>[],
  draggedNode: Node<EpisodeFlowNodeData>,
): { readonly optionLine: number; readonly distance: number } | null {
  const draggedSize = approxFlowNodeSize(draggedNode);
  const draggedCenter = {
    x: draggedNode.position.x + draggedSize.width / 2,
    y: draggedNode.position.y + draggedSize.height / 2,
  };
  let best: { optionLine: number; distance: number } | null = null;

  for (const node of nodes) {
    if (node.data.nodeKind !== "branch") continue;
    const optionLine = optionLineFromBranchNode(node);
    if (optionLine === null) continue;
    const size = approxFlowNodeSize(node);
    const center = {
      x: node.position.x + size.width / 2,
      y: node.position.y + size.height / 2,
    };
    const dx = draggedCenter.x - center.x;
    const dy = draggedCenter.y - center.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (!best || distance < best.distance) best = { optionLine, distance };
  }

  return best && best.distance <= 240 ? best : null;
}

function sourceRangeForVisualBlock(source: string, blocks: readonly LsBlock[], visualBlock: EpisodeVisualBlock): LineRange {
  const firstIndex = blocks.findIndex((block) => block.line === visualBlock.line);
  const first = blocks[firstIndex];
  const range = first && COMPOUND_BLOCK_KINDS.has(first.kind)
    ? blockLineRange(source, blocks, firstIndex)
    : { start: visualBlock.line, end: visualBlock.endLine };
  return expandRangeToLeadingCanvasMarker(source, range);
}

function expandRangeToLeadingCanvasMarker(source: string, range: LineRange): LineRange {
  const previousLine = source.split(/\r?\n/)[range.start - 2];
  if (previousLine?.trim() !== CANVAS_BLOCK_MARKER) return range;
  return { ...range, start: range.start - 1 };
}

function sourceLineIndent(source: string, line: number): string {
  return source.split(/\r?\n/)[line - 1]?.match(/^\s*/)?.[0] ?? "";
}

function sourceTextForRange(source: string, range: LineRange): string {
  return source.split(/\r?\n/).slice(range.start - 1, range.end).join("\n");
}

function lastStandaloneCloseLineInSource(source: string): number | undefined {
  const lines = source.split(/\r?\n/);
  for (let i = lines.length - 1; i >= 0; i--) {
    if ((lines[i] ?? "").trim() === "}") return i + 1;
  }
  return undefined;
}

function lineInsideGateRange(source: string, blocks: readonly LsBlock[], line: number): boolean {
  return blocks.some((block, index) => {
    if (block.kind !== "gate") return false;
    const range = blockLineRange(source, blocks, index);
    return line >= range.start && line <= range.end;
  });
}

function reindentCopiedLines(lines: readonly string[], targetIndent: string): string[] {
  const baseIndent = lines.find((line) => line.trim() !== "")?.match(/^\s*/)?.[0] ?? "";
  return lines.map((line) => {
    if (line.trim() === "") return line;
    if (baseIndent && line.startsWith(baseIndent)) return `${targetIndent}${line.slice(baseIndent.length)}`;
    return `${targetIndent}${line.trimStart()}`;
  });
}

function firstMeaningfulTrimmedLine(lines: readonly string[]): string {
  return lines.find((line) => line.trim() !== "")?.trim() ?? "";
}

function shouldPrefixCanvasMarker(lines: readonly string[]): boolean {
  const trimmed = firstMeaningfulTrimmedLine(lines);
  if (!trimmed) return false;
  if (trimmed === CANVAS_BLOCK_MARKER) return false;
  if (trimmed.startsWith("@episode")) return false;
  if (trimmed.startsWith("@gate")) return false;
  return true;
}

export interface CanvasClipboardPayload {
  readonly text: string;
}

export function canvasClipboardPayloadForLine(
  source: string,
  blocks: readonly LsBlock[],
  visualBlocks: readonly Pick<EpisodeVisualBlock, "line" | "endLine" | "blocks" | "choice" | "gate">[],
  selectedLine: number | null | undefined,
): CanvasClipboardPayload | null {
  if (selectedLine == null) return null;
  const visual = visualBlocks.find((block) => lineInVisualBlock(selectedLine, block as EpisodeVisualBlock));
  const blockIndex = blocks.findIndex((block) => block.line === selectedLine);
  if (!visual && blockIndex < 0) return null;

  const range =
    visual && selectedLine === visual.line
      ? sourceRangeForVisualBlock(source, blocks, visual as EpisodeVisualBlock)
      : expandRangeToLeadingCanvasMarker(source, blockLineRange(source, blocks, blockIndex));
  const text = sourceTextForRange(source, range);
  return text.trim() ? { text } : null;
}

export function pasteCanvasClipboardAfterLine(
  source: string,
  blocks: readonly LsBlock[],
  payload: CanvasClipboardPayload | null,
  afterLine: number | null | undefined,
): { readonly source: string; readonly insertedLine: number | null; readonly changed: boolean } {
  if (!payload?.text.trim()) return { source, insertedLine: null, changed: false };
  const copiedLines = payload.text.split(/\r?\n/);
  const copiedStartsWithOption = firstMeaningfulTrimmedLine(copiedLines).startsWith("@option");
  let targetLine: number | undefined;
  let targetIndent = "";

  if (afterLine != null) {
    const anchorIndex = blocks.findIndex((block) => block.line === afterLine);
    const anchor = blocks[anchorIndex];
    if (lineInsideGateRange(source, blocks, afterLine)) {
      targetLine = undefined;
    } else if (anchor && anchor.kind !== "episode") {
      const range = blockLineRange(source, blocks, anchorIndex);
      if (anchor.kind === "option" && copiedStartsWithOption) {
        targetLine = range.end + 1;
        targetIndent = sourceLineIndent(source, anchor.line);
      } else if (anchor.kind === "option") {
        targetLine = range.end;
        targetIndent = `${sourceLineIndent(source, anchor.line)}  `;
      } else if (!copiedStartsWithOption) {
        targetLine = range.end + 1;
        targetIndent = sourceLineIndent(source, range.start);
      }
    }
  }

  if (copiedStartsWithOption && targetLine === undefined) return { source, insertedLine: null, changed: false };

  if (targetLine === undefined) {
    const gate = blocks.find((block) => block.kind === "gate");
    if (gate) {
      targetLine = gate.line;
      targetIndent = sourceLineIndent(source, gate.line);
    } else {
      const closeLine = lastStandaloneCloseLineInSource(source);
      targetLine = closeLine ?? source.split(/\r?\n/).length + 1;
      targetIndent = closeLine ? `${sourceLineIndent(source, closeLine)}  ` : "";
    }
  }

  const pasted = reindentCopiedLines(copiedLines, targetIndent);
  const withMarker = shouldPrefixCanvasMarker(pasted) ? [`${targetIndent}${CANVAS_BLOCK_MARKER}`, ...pasted] : pasted;
  const next = insertLine(source, targetLine, withMarker.join("\n"));
  return {
    source: next,
    insertedLine: targetLine + (withMarker[0]?.trim() === CANVAS_BLOCK_MARKER ? 1 : 0),
    changed: next !== source,
  };
}

function deleteVisualBlockByLineWithResult(
  source: string,
  blocks: readonly LsBlock[],
  visualBlocks: readonly EpisodeVisualBlock[],
  line: number | null | undefined,
): { readonly source: string; readonly changed: boolean } {
  if (line == null) return { source, changed: false };
  const visual = visualBlocks.find((block) => lineInVisualBlock(line, block));
  if (!visual || visual.kind === "route" || visual.gate) {
    const result = deleteBlockByLineWithResult(source, blocks, line);
    return { source: result.source, changed: result.changed };
  }
  if (line !== visual.line || visual.choice || visual.blocks.length <= 1) {
    const result = deleteBlockByLineWithResult(source, blocks, line);
    return { source: result.source, changed: result.changed };
  }
  const range = expandRangeToLeadingCanvasMarker(source, sourceRangeForVisualBlock(source, blocks, visual));
  const next = deleteLines(source, range.start, range.end);
  return { source: next, changed: next !== source };
}

function translatedInsertOptions(
  tr: ReturnType<typeof useTr>,
  disabledReason: (kind: InsertKind) => string | undefined,
): EpisodeFlowInsertOption[] {
  return INSERT_OPTIONS.map((opt) => ({
    kind: opt.kind,
    label: tr(opt.label),
    token: opt.token,
    disabledReason: disabledReason(opt.kind),
    ...(opt.kind === "minigame"
      ? {
          subOptions: minigamePresetInsertOptions().map((preset) => ({
            kind: preset.kind,
            label: tr(preset.label),
            token: preset.token,
            disabledReason: disabledReason(preset.kind),
          })),
        }
      : {}),
  }));
}

function optionIdFromNode(node: Node<EpisodeFlowNodeData> | undefined): string | null {
  if (!node || node.data.nodeKind !== "option") return null;
  const optionId = node.data.optionId;
  return typeof optionId === "string" && optionId.trim() ? optionId : null;
}

function optionIdFromOutgoingNode(node: Node<EpisodeFlowNodeData> | undefined): string | null {
  if (!node) return null;
  if (node.data.nodeKind === "option" || node.data.nodeKind === "branch") {
    const optionId = node.data.optionId;
    return typeof optionId === "string" && optionId.trim() ? optionId : null;
  }
  return null;
}

function optionLineFromNode(node: Node<EpisodeFlowNodeData> | undefined): number | null {
  if (!node || node.data.nodeKind !== "option") return null;
  return typeof node.data.line === "number" ? node.data.line : null;
}

function optionLineFromBranchNode(node: Node<EpisodeFlowNodeData> | undefined): number | null {
  if (!node || node.data.nodeKind !== "branch") return null;
  return typeof node.data.optionLine === "number" ? node.data.optionLine : null;
}

function optionLineFromOutgoingNode(node: Node<EpisodeFlowNodeData> | undefined): number | null {
  return optionLineFromNode(node) ?? optionLineFromBranchNode(node);
}

function routeTargetFromNode(node: Node<EpisodeFlowNodeData> | undefined): GateRouteConnection["target"] | null {
  if (!node || node.data.nodeKind !== "target") return null;
  const kind = node.data.routeOutcomeKind;
  const value = node.data.routeOutcomeValue;
  if ((kind === "next" || kind === "end") && typeof value === "string" && value.trim()) {
    return { kind, value };
  }
  return null;
}

function routeResultFromNode(node: Node<EpisodeFlowNodeData> | undefined): GateRouteConnection["result"] | undefined {
  const result = node?.data.routeResult;
  return result === "success" || result === "fail" || result === "any" ? result : undefined;
}

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const editable = target.closest("[contenteditable]");
  if (editable instanceof HTMLElement && editable.isContentEditable) return true;
  return Boolean(
    target.closest(
      "input,textarea,select,[role='textbox'],.lc-route-connect,.lc-flow-insert-menu,.lc-canvas-palette,.lc-canvas-floating-inspector",
    ),
  );
}

function isCanvasShortcutScope(target: EventTarget | null): boolean {
  const targetElement = target instanceof Element ? target : null;
  if (targetElement?.closest(".lw-agent")) return false;
  if (targetElement?.closest(".lc-canvas-studio")) return true;
  const activeElement = typeof document !== "undefined" && document.activeElement instanceof Element ? document.activeElement : null;
  return Boolean(activeElement?.closest(".lc-canvas-studio")) && !activeElement?.closest(".lw-agent");
}

function isPlatformShortcut(event: KeyboardEvent, key: string): boolean {
  return event.key.toLowerCase() === key && (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey;
}

function EpisodeFlowLevel({
  scenes,
  blocks,
  source,
  onChange,
  structure,
  selectedLine,
  onSelectLine,
  onInsertBlock,
  onUndoSourceChange,
  canUndoSourceChange,
  onCreateScene,
  activeId,
}: {
  scenes: readonly CanvasScene[];
  activeId?: string;
  blocks: readonly LsBlock[];
  source: string;
  onChange?: (source: string) => void;
  structure?: EpisodeStructure;
  selectedLine?: number | null;
  onSelectLine: (line: number | null) => void;
  onInsertBlock?: (kind: InsertKind, afterLine?: number | null) => void;
  onUndoSourceChange?: () => void;
  canUndoSourceChange?: boolean;
  onCreateScene?: (branchKey: string) => void;
}) {
  const tr = useTr();
  const { fitView, getViewport, screenToFlowPosition, setViewport } = useReactFlow();
  const viewport = useViewport();
  const zoomTier = viewport.zoom <= 0.55 ? "overview" : viewport.zoom <= 0.8 ? "scan" : "detail";
  const [draggingInsertKind, setDraggingInsertKind] = useState<InsertKind | null>(null);
  const [insertPreviewLine, setInsertPreviewLine] = useState<number | null>(null);
  const [reorderPreview, setReorderPreview] = useState<{
    readonly blockId: string;
    readonly placement: "before" | "after";
  } | null>(null);
  const [canvasClipboard, setCanvasClipboard] = useState<CanvasClipboardPayload | null>(null);
  const [viewportSizeKey, setViewportSizeKey] = useState<string>("");
  const visualBlocks = useMemo(() => buildEpisodeVisualBlocks(blocks, structure, source), [blocks, source, structure]);
  const routeTargetIndex = useMemo(
    () => routeTargetIndexForScenes(scenes, tr, structure?.meta.branchKey || activeId),
    [activeId, scenes, structure?.meta.branchKey, tr],
  );

  const insertInsideOption = useCallback(
    (optionLine: number, kind: InsertKind) => {
      if (!onChange) return;
      const result = insertBlockInsideOptionWithResult(source, blocks, optionLine, kind);
      if (result.source !== source) onChange(result.source);
      onSelectLine(result.insertedLine ?? optionLine);
    },
    [blocks, onChange, onSelectLine, source],
  );

  const graphKey = useMemo(
    () => visualBlocks.map((block) => `${block.id}:${block.line}:${block.endLine}`).join("|"),
    [visualBlocks],
  );

  const deleteLineFromCanvas = useCallback(
    (line: number) => {
      if (!onChange) return;
      const result = deleteVisualBlockByLineWithResult(source, blocks, visualBlocks, line);
      if (!result.changed) return;
      onChange(result.source);
      onSelectLine(null);
    },
    [blocks, onChange, onSelectLine, source, visualBlocks],
  );

  const { nodes: baseNodes, edges: baseEdges } = useMemo(() => {
    const flowNodes: Node<EpisodeFlowNodeData>[] = [];
    const flowEdges: Edge<StoryEdgeData>[] = [];
    const choices = structure?.choices ?? [];
    const fallbackRoute = fallbackRouteForGate(structure?.gate);
    const { knownBranchKeys } = routeTargetIndex;
    const mainX = -FLOW_MAIN_NODE_WIDTH / 2;
    let mainY = FLOW_TOP_PADDING;

    const hasGate = blocks.some((block) => block.kind === "gate");
    const selectedHasAncestor = (line: number, kind: LsBlock["kind"]): boolean => {
      const blockIndex = blocks.findIndex((block) => block.line === line);
      const block = blocks[blockIndex];
      if (blockIndex < 0 || !block) return false;
      for (let i = blockIndex - 1; i >= 0; i--) {
        const candidate = blocks[i]!;
        if (candidate.depth >= block.depth) continue;
        if (candidate.kind === kind) return true;
      }
      return false;
    };
    const disabledReasonForAnchor = (line: number, kind: InsertKind): string | undefined => {
      if (kind === "choice" && (selectedHasAncestor(line, "choice") || selectedHasAncestor(line, "gate"))) return tr(S.choiceNested);
      if (kind === "gate" && hasGate) return tr(S.gateExists);
      return undefined;
    };

    const addNode = (
      id: string,
      x: number,
      y: number,
      data: EpisodeFlowNodeData,
    ) => {
      const isInsert = data.nodeKind === "insert";
      const isLane = data.nodeKind === "lane";
      const isReorderable = data.nodeKind === "block" || data.nodeKind === "choice";
      const isMovable = !isLane && !isInsert && data.nodeKind !== "branch";
      const isConnectable =
        data.sourceConnectable === true ||
        data.targetConnectable === true ||
        data.nodeKind === "option" ||
        data.nodeKind === "target" ||
        data.sequenceConnectable === true;
      flowNodes.push({
        id,
        type: "episode",
        position: { x, y },
        data: {
          ...data,
          reorderable: isReorderable,
          onDeleteLine:
            !isLane && !isInsert && typeof data.line === "number" && onChange
              ? deleteLineFromCanvas
              : data.onDeleteLine,
        },
        selectable: !isLane,
        draggable: isMovable,
        connectable: isConnectable,
        zIndex: isLane ? 0 : isInsert ? 3 : 2,
        style: isLane ? { pointerEvents: "none" } : undefined,
      });
    };

    const addEdge = (
      id: string,
      source: string,
      target: string,
      kind: StoryEdgeData["kind"],
      label?: string,
    ) => {
      flowEdges.push({
        id,
        source,
        target,
        sourceHandle: "source-bottom",
        targetHandle: "target-top",
        type: "story",
        label,
        data: { kind },
        markerEnd: { type: MarkerType.ArrowClosed, width: FLOW_ARROW_MARKER_SIZE, height: FLOW_ARROW_MARKER_SIZE, color: markerColorFor(kind) },
        interactionWidth: 24,
      });
    };

    const addInsertSlot = (block: EpisodeVisualBlock, y: number) => {
      if (!onInsertBlock || block.kind === "route") return;
      const anchorLine = insertAnchorLineForVisualBlock(block);
      const slotId = `insert:${block.id}`;
      const blockHeight = estimateFlowNodeHeight(block, fallbackRoute) - FLOW_MAIN_GAP;
      const insertX = FLOW_MAIN_LINE_X + FLOW_INSERT_LINE_SIDE_GAP;
      const insertY = y + blockHeight + FLOW_MAIN_GAP / 2 - FLOW_INSERT_WIDTH / 2;
      addNode(slotId, insertX, insertY, {
        nodeKind: "insert",
        icon: "+",
        label: tr(S.addDockTitle),
        title: tr(S.insertAfterBlock),
        summary: "",
        tone: "var(--accent-rose)",
        line: anchorLine,
        insertOptions: translatedInsertOptions(tr, (kind) => disabledReasonForAnchor(anchorLine, kind)),
        dropActive: insertPreviewLine === anchorLine,
        onInsert: (kind) => {
          onInsertBlock(kind, anchorLine);
        },
      });
    };

    let previousMainBlock: EpisodeVisualBlock | null = null;
    let previousMainNodeId: string | null = null;
    let previousChoiceExitNodes: { readonly nodeId: string; readonly label: string }[] = [];

    visualBlocks.forEach((block) => {
      const y = mainY;
      const nodeId = `block:${block.id}`;
      const nodeKind: EpisodeFlowNodeData["nodeKind"] =
        block.kind === "choice" ? "choice" : block.kind === "route" ? "route" : "block";
      addNode(nodeId, mainX, y, {
        nodeKind,
        icon: block.kind === "choice" ? "?" : block.kind === "route" ? "→" : "¶",
        label: tr(visualKindLabel(block.kind)),
        title: block.title,
        summary: block.summary,
        tone: visualToneFor(block.kind),
        line: block.line,
        selected: lineInVisualBlock(selectedLine, block),
        dropPlacement: reorderPreview?.blockId === block.id ? reorderPreview.placement : undefined,
        sequenceConnectable: canReorderVisualBlock(block),
        steps: stepsForFlowNode(block),
        onSelectLine,
      });

      if (previousMainBlock?.choice && previousChoiceExitNodes.length > 0) {
        previousChoiceExitNodes.forEach((exit) => {
          addEdge(
            `choice-exit:${exit.nodeId}:${block.id}`,
            exit.nodeId,
            nodeId,
            "next",
            block.kind === "route" ? undefined : exit.label,
          );
        });
      } else if (previousMainBlock && previousMainNodeId) {
        addEdge(`seq:${previousMainBlock.id}:${block.id}`, previousMainNodeId, nodeId, "next");
      }

      if (block.choice) {
        const optionCount = block.choice.options.length;
        const optionY = y + FLOW_OPTION_TOP;
        const branchY = optionY + optionBranchTopForChoice(block.choice);
        const exitNodes: { readonly nodeId: string; readonly label: string }[] = [];
        block.choice.options.forEach((option, optionIndex) => {
          const optionNodeId = `option:${block.id}:${option.line}`;
          const branchNodeId = `branch:${block.id}:${option.line}`;
          const laneCenterX = (optionIndex - (optionCount - 1) / 2) * FLOW_BRANCH_GAP;
          const x = laneCenterX - FLOW_OPTION_NODE_WIDTH / 2;
          const branchBlocks = blocksInsideRange(blocks, option.range).filter((item) => item.kind !== "check" && item.kind !== "param");
          const branchSteps = optionBodySteps(blocks, option);
          const impacts = optionImpactLabels(option, tr);
          addNode(optionNodeId, x, optionY, {
            nodeKind: "option",
            icon: option.id ?? String(optionIndex + 1),
            label: option.id ? `Option ${option.id}` : `Option ${optionIndex + 1}`,
            title: option.text || "Untitled option",
            summary: impacts.length > 0 ? undefined : undefined,
            tone: flowNodeTone("option"),
            line: option.line,
            selected: selectedLine === option.line,
            badges: optionBadges(option, tr),
            impacts,
            muted: impacts.length === 0 ? tr(S.moveIntoOption) : undefined,
            optionId: option.id,
            onSelectLine,
          });
          addEdge(`choice:${block.id}:${option.line}`, nodeId, optionNodeId, "choice", option.id ?? String(optionIndex + 1));
          addNode(branchNodeId, laneCenterX - FLOW_BRANCH_NODE_WIDTH / 2, branchY, {
            nodeKind: "branch",
            icon: "→",
            label: tr(S.optionBranch),
            title: option.text ? clipText(option.text, 30) : (option.id ? `Option ${option.id}` : `Option ${optionIndex + 1}`),
            summary: branchBlocks.length > 0 ? undefined : tr(S.emptyOptionBranch),
            tone: "var(--kind-route-target)",
            line: branchBlocks[0]?.line ?? option.line,
            selected: typeof selectedLine === "number" && selectedLine > option.range.start && selectedLine < option.range.end,
            steps: branchSteps,
            muted: branchSteps.length === 0 ? tr(S.emptyOptionBranch) : undefined,
            branchInsertLabel: tr(S.addOptionBranchContent),
            insertOptions: translatedInsertOptions(tr, (kind) => disabledReasonForAnchor(option.line, kind)),
            optionId: option.id,
            optionLine: option.line,
            sourceConnectable: true,
            onInsert: (kind) => insertInsideOption(option.line, kind),
            onSelectLine,
          });
          addEdge(`option-branch:${block.id}:${option.line}`, optionNodeId, branchNodeId, "next");
          exitNodes.push({ nodeId: branchNodeId, label: option.id ?? String(optionIndex + 1) });
        });
        previousChoiceExitNodes = exitNodes;
      } else {
        previousChoiceExitNodes = [];
      }

      if (block.gate) {
        const routeCount = block.gate.routes.length;
        const routeY = y + FLOW_ROUTE_TARGET_TOP;
        block.gate.routes.forEach((route, routeIndex) => {
          const targetNodeId = `gate-target:${block.id}:${route.index}`;
          const x = (routeIndex - (routeCount - 1) / 2) * FLOW_BRANCH_GAP - FLOW_TARGET_NODE_WIDTH / 2;
          const condition = routeConditionText(route, choices, tr(S.fallback), tr(S.direct));
          addNode(targetNodeId, x, routeY, {
            nodeKind: "target",
            icon: route.outcome.kind === "end" ? "■" : String(route.index + 1),
            label: condition,
            title: outcomeLabel(route),
            ...targetNodeRouteData(route, knownBranchKeys, tr, onCreateScene),
            tone: targetToneForRoute(route),
            line: route.line,
            routeOutcomeKind: route.outcome.kind,
            routeOutcomeValue: route.outcome.value,
            selected: selectedLine === route.line,
            onSelectLine,
          });
          addEdge(`gate:${block.id}:${route.index}`, nodeId, targetNodeId, route.isFallback ? "next" : "gate");
        });
      }

      addInsertSlot(block, y);
      mainY += estimateFlowNodeHeight(block, fallbackRoute);
      previousMainBlock = block;
      previousMainNodeId = nodeId;
    });

    return { nodes: flowNodes, edges: flowEdges };
  }, [
    blocks,
    deleteLineFromCanvas,
    insertPreviewLine,
    onCreateScene,
    onInsertBlock,
    onSelectLine,
    reorderPreview,
    routeTargetIndex,
    selectedLine,
    structure,
    tr,
    visualBlocks,
  ]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<EpisodeFlowNodeData>>(baseNodes);
  const initialViewportKey = useRef<string>("");
  const previousGraphKey = useRef<string>(graphKey);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let frame = 0;
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        setViewportSizeKey(`${window.innerWidth}x${window.innerHeight}`);
      });
    };
    update();
    window.addEventListener("resize", update);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    const graphChanged = previousGraphKey.current !== graphKey;
    previousGraphKey.current = graphKey;
    setNodes((previous) => {
      const previousById = new Map(previous.map((node) => [node.id, node]));
      return baseNodes.map((node) => {
        const prev = previousById.get(node.id);
        return {
          ...node,
          position: graphChanged ? node.position : (prev?.position ?? node.position),
          data: node.data,
        };
      });
    });
  }, [baseNodes, graphKey, setNodes]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!viewportSizeKey) return;
    const viewportKey = `${graphKey}:${viewportSizeKey}`;
    if (initialViewportKey.current === viewportKey) return;
    initialViewportKey.current = viewportKey;
    const timer = window.setTimeout(() => {
      const isSmall = window.matchMedia("(max-width: 640px)").matches;
      const isTightVertical = !isSmall && window.innerHeight < 700;
      const widestChoice = Math.max(1, ...visualBlocks.map((block) => block.choice?.options.length ?? 1));
      const hasRouteTable = visualBlocks.some((block) => block.kind === "route");
      const hasChoiceRouteSidecar = false;
      const maxZoom = isSmall ? (widestChoice <= 2 ? 0.7 : 0.58) : widestChoice <= 2 ? 0.9 : widestChoice <= 4 ? 0.72 : 0.58;
      const minZoom = isSmall
        ? (hasChoiceRouteSidecar ? 0.39 : hasRouteTable ? (widestChoice <= 2 ? 0.5 : 0.42) : widestChoice <= 2 ? 0.48 : 0.42)
        : isTightVertical && hasChoiceRouteSidecar
          ? 0.35
        : hasChoiceRouteSidecar
          ? (widestChoice <= 2 ? 0.52 : 0.46)
        : hasRouteTable
          ? (widestChoice <= 2 ? 0.46 : 0.42)
          : widestChoice <= 2 ? 0.54 : widestChoice <= 4 ? 0.46 : 0.4;
      void fitView({
        padding: isSmall ? 0.24 : isTightVertical ? 0.08 : 0.34,
        duration: 0,
        minZoom,
        maxZoom,
        nodes: baseNodes
          .filter((node) => node.data.nodeKind !== "lane")
          .map((node) => ({ id: node.id })),
      }).then(() => {
        const current = getViewport();
        void setViewport({ ...current, y: current.y + (isSmall ? 260 : isTightVertical ? 22 : 40) }, { duration: 0 });
      });
    }, 60);
    return () => {
      window.clearTimeout(timer);
    };
  }, [baseNodes, fitView, getViewport, graphKey, setViewport, viewportSizeKey, visualBlocks]);

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target || connection.source === connection.target) return;
      if (connection.sourceHandle !== "source-bottom" || connection.targetHandle !== "target-top") return;
      const sourceNode = nodes.find((node) => node.id === connection.source);
      const targetNode = nodes.find((node) => node.id === connection.target);
      const sourceOptionLine = optionLineFromOutgoingNode(sourceNode);
      const targetBlockIdForOption = visualBlockIdFromNodeId(connection.target);
      if (sourceOptionLine !== null && targetBlockIdForOption && onChange) {
        const targetBlock = visualBlocks.find((block) => block.id === targetBlockIdForOption);
        if (targetBlock && canMoveVisualBlockIntoOption(targetBlock)) {
          const result = moveLineRangeIntoOptionWithResult(
            source,
            blocks,
            sourceRangeForVisualBlock(source, blocks, targetBlock),
            sourceOptionLine,
          );
          if (result.changed) onChange(result.source);
          onSelectLine(result.movedLine ?? targetBlock.line);
          return;
        }
      }
      const optionId = optionIdFromOutgoingNode(sourceNode);
      const routeTarget = routeTargetFromNode(targetNode);
      const routeResult = routeResultFromNode(targetNode);

      if (optionId && routeTarget && onChange) {
        const result = connectChoiceOptionToRoute(
          source,
          blocks,
          routeResult ? { optionId, result: routeResult, target: routeTarget } : { optionId, target: routeTarget },
        );
        if (result.changed) onChange(result.source);
        onSelectLine(result.line ?? sourceNode?.data.line ?? targetNode?.data.line ?? null);
        return;
      }

      const sourceBlockId = visualBlockIdFromNodeId(connection.source);
      const targetBlockId = visualBlockIdFromNodeId(connection.target);
      if (sourceBlockId && targetBlockId && sourceBlockId !== targetBlockId && onChange) {
        const sourceBlock = visualBlocks.find((block) => block.id === sourceBlockId);
        const targetBlock = visualBlocks.find((block) => block.id === targetBlockId);
        if (
          sourceBlock &&
          targetBlock &&
          canReorderVisualBlock(sourceBlock) &&
          canReorderVisualBlock(targetBlock)
        ) {
          const next = moveLineRangeRelative(
            source,
            sourceRangeForVisualBlock(source, blocks, targetBlock),
            sourceRangeForVisualBlock(source, blocks, sourceBlock),
            "after",
          );
          if (next !== source) onChange(next);
          onSelectLine(targetBlock.line);
          return;
        }
      }

      if (typeof sourceNode?.data.line === "number") onSelectLine(sourceNode.data.line);
    },
    [blocks, nodes, onChange, onSelectLine, source, visualBlocks],
  );

  const handleNodeDragStop = useCallback<OnNodeDrag<Node<EpisodeFlowNodeData>>>(
    (_event, node) => {
      setReorderPreview(null);
      const draggedId = visualBlockIdFromNodeId(node.id);
      const draggedBlock = draggedId ? visualBlocks.find((block) => block.id === draggedId) : undefined;
      if (!onChange || !draggedBlock) {
        return;
      }

      if (canMoveVisualBlockIntoOption(draggedBlock)) {
        const branchTarget = nearestBranchDropTarget(nodes, node);
        if (branchTarget) {
          const result = moveLineRangeIntoOptionWithResult(
            source,
            blocks,
            sourceRangeForVisualBlock(source, blocks, draggedBlock),
            branchTarget.optionLine,
          );
          if (!result.changed) return;
          onChange(result.source);
          onSelectLine(result.movedLine ?? draggedBlock.line);
          return;
        }
      }

      const target = nearestReorderTarget(nodes, visualBlocks, node);
      if (!target) {
        return;
      }

      const next = moveLineRangeRelative(
        source,
        sourceRangeForVisualBlock(source, blocks, draggedBlock),
        sourceRangeForVisualBlock(source, blocks, target.target),
        target.placement,
      );

      if (next === source) {
        return;
      }

      onChange(next);
      onSelectLine(null);
    },
    [blocks, nodes, onChange, onSelectLine, source, visualBlocks],
  );

  const handleNodeDrag = useCallback<OnNodeDrag<Node<EpisodeFlowNodeData>>>(
    (_event, node) => {
      const target = nearestReorderTarget(nodes, visualBlocks, node);
      const next = target ? { blockId: target.target.id, placement: target.placement } : null;
      setReorderPreview((previous) => {
        if (!previous && !next) return previous;
        if (previous && next && previous.blockId === next.blockId && previous.placement === next.placement) return previous;
        return next;
      });
    },
    [nodes, visualBlocks],
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      const kind = event.dataTransfer.getData(INSERT_DRAG_MIME) as InsertKind;
      if (!kind || !onInsertBlock) return;
      event.preventDefault();
      setDraggingInsertKind(null);
      setInsertPreviewLine(null);
      const point = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      const anchorLine = nearestAnchorLine(nodes, point) ?? selectedLine ?? null;
      onInsertBlock(kind, anchorLine);
    },
    [nodes, onInsertBlock, screenToFlowPosition, selectedLine],
  );

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!Array.from(event.dataTransfer.types).includes(INSERT_DRAG_MIME)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    const kind = event.dataTransfer.getData(INSERT_DRAG_MIME) as InsertKind;
    setDraggingInsertKind(kind || "dialogue");
    const point = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    setInsertPreviewLine(nearestAnchorLine(nodes, point) ?? selectedLine ?? null);
  }, [nodes, screenToFlowPosition, selectedLine]);

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (relatedTarget && event.currentTarget.contains(relatedTarget)) return;
    setDraggingInsertKind(null);
    setInsertPreviewLine(null);
  }, []);

  const handlePaneClick = useCallback(
    (event: MouseEvent<Element>) => {
      const target = event.target instanceof Element ? event.target : null;
      if (
        target?.closest(
          ".lc-route-connect,.lc-flow-insert-menu,.lc-canvas-palette,.lc-canvas-floating-inspector,.nodrag,.nopan,button,input,textarea,select,[role='button']",
        )
      ) {
        return;
      }
      onSelectLine(null);
    },
    [onSelectLine],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (!isCanvasShortcutScope(event.target)) return;
      const editingTarget = isTextEditingTarget(event.target);
      if (event.key === "Escape") {
        onSelectLine(null);
        return;
      }
      if (isPlatformShortcut(event, "z") && canUndoSourceChange && onUndoSourceChange && !editingTarget) {
        event.preventDefault();
        event.stopPropagation();
        onUndoSourceChange();
        return;
      }
      if (isPlatformShortcut(event, "c") && selectedLine != null && !editingTarget) {
        const payload = canvasClipboardPayloadForLine(source, blocks, visualBlocks, selectedLine);
        if (!payload) return;
        event.preventDefault();
        event.stopPropagation();
        setCanvasClipboard(payload);
        void navigator.clipboard?.writeText(payload.text).catch(() => undefined);
        return;
      }
      if (isPlatformShortcut(event, "v") && canvasClipboard && onChange && !editingTarget) {
        const result = pasteCanvasClipboardAfterLine(source, blocks, canvasClipboard, selectedLine);
        if (!result.changed) return;
        event.preventDefault();
        event.stopPropagation();
        onChange(result.source);
        onSelectLine(result.insertedLine ?? selectedLine ?? null);
        return;
      }
      if ((event.key === "Delete" || event.key === "Backspace") && onChange && selectedLine != null) {
        if (editingTarget) return;
        const result = deleteVisualBlockByLineWithResult(source, blocks, visualBlocks, selectedLine);
        if (!result.changed) return;
        event.preventDefault();
        event.stopPropagation();
        onChange(result.source);
        onSelectLine(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    blocks,
    canUndoSourceChange,
    canvasClipboard,
    onChange,
    onSelectLine,
    onUndoSourceChange,
    selectedLine,
    source,
    visualBlocks,
  ]);

  if (visualBlocks.length === 0) {
    return (
      <div className="lc-episode-board lc-episode-board--empty">
        <EmptyState
          icon={<Clapperboard size={22} aria-hidden />}
          title={tr(S.emptyTitle)}
          description={tr(S.emptyBody)}
        />
      </div>
    );
  }

  return (
    <div className="lc-episode-flow" data-zoom-tier={zoomTier}>
      <EpisodeFlowSemanticSummary nodes={nodes} edges={baseEdges} />
      <ReactFlow
        nodes={nodes}
        edges={baseEdges}
        nodeTypes={episodeNodeTypes}
        edgeTypes={episodeEdgeTypes}
        onNodesChange={onNodesChange}
        onConnect={handleConnect}
        onNodeDrag={handleNodeDrag}
        onNodeDragStop={handleNodeDragStop}
        onPaneClick={handlePaneClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        minZoom={0.35}
        maxZoom={1.5}
        nodesDraggable
        nodesConnectable
        deleteKeyCode={null}
        selectNodesOnDrag={false}
        connectionMode={ConnectionMode.Strict}
        elementsSelectable
        panOnScroll
        panOnScrollMode={PanOnScrollMode.Free}
        panOnScrollSpeed={0.7}
        panOnDrag
        paneClickDistance={4}
        nodeClickDistance={4}
        zoomActivationKeyCode={["Meta", "Control"]}
        zoomOnScroll={false}
        zoomOnPinch
        zoomOnDoubleClick={false}
        proOptions={{ hideAttribution: true }}
        className="lc-canvas__flow"
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1.3} color="var(--border-subtle)" />
        <Panel position="top-right" className="lc-canvas__toolbar">
          <FlowZoomControls
            onFit={() => {
              const isSmall = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
              const isTightVertical = typeof window !== "undefined" && !isSmall && window.innerHeight < 700;
              void fitView({
                padding: isSmall ? 0.24 : isTightVertical ? 0.08 : 0.32,
                duration: 240,
                maxZoom: 1,
                nodes: nodes
                  .filter((node) => node.data.nodeKind !== "lane")
                  .map((node) => ({ id: node.id })),
              }).then(() => {
                const current = getViewport();
                void setViewport({ ...current, y: current.y + (isSmall ? 260 : isTightVertical ? 22 : 40) }, { duration: 80 });
              });
            }}
          />
        </Panel>
        <Panel position="bottom-left" className="lc-canvas-gesture-legend">
          <Badge variant="tonal" tone="neutral" size="md">
            <Move size={13} aria-hidden="true" /> {tr(S.canvasMove)}
          </Badge>
          <Badge variant="tonal" tone="neutral" size="md">
            <SlidersHorizontal size={13} aria-hidden="true" /> {tr(S.canvasZoomGesture)}
          </Badge>
          <Badge variant="tonal" tone="neutral" size="md">
            <Link2 size={13} aria-hidden="true" /> {tr(S.canvasConnect)}
          </Badge>
        </Panel>
        {draggingInsertKind ? (
          <Panel position="top-center" className="lc-canvas-drop-hint">
            <Badge variant="tonal" tone="success" size="md">
              <Plus size={13} aria-hidden="true" /> {tr(S.dropToInsert)}
            </Badge>
          </Panel>
        ) : null}
      </ReactFlow>
    </div>
  );
}

function ChoiceRouteList({
  choice,
  selectedLine,
  onSelectLine,
  renderEditor,
}: {
  choice: ChoiceStructure;
  selectedLine?: number | null;
  onSelectLine: (line: number) => void;
  renderEditor?: (line: number) => ReactNode;
}) {
  const tr = useTr();
  return (
    <div className="lc-choice-routes" role="list" aria-label={tr(S.directRoute)}>
      {choice.options.map((option) => {
        const active = selectedLine === option.line;
        const check = checkLabel(option);
        const modeLabel = optionModeLabel(option);
        return (
          <div
            key={`${option.line}:${option.id ?? option.text ?? "option"}`}
            className="lc-choice-route-row"
            data-active={active || undefined}
            role="listitem"
          >
            <button
              type="button"
              className="lc-choice-route"
              data-active={active || undefined}
              aria-expanded={active ? "true" : "false"}
              onClick={(event) => {
                event.stopPropagation();
                onSelectLine(option.line);
              }}
            >
              <span className="lc-choice-route__head">
                <span className="lc-choice-route__id">{option.id ?? "?"}</span>
                <span className="lc-choice-route__label">{option.text || "Untitled option"}</span>
                {modeLabel ? <span className="lc-choice-route__mode">{tr(modeLabel)}</span> : null}
                {check ? <span className="lc-choice-route__check">{tr(S.check)} {check}</span> : null}
              </span>
              {option.effects.length > 0 ? (
                <span className="lc-choice-route__effects" aria-label={tr(S.effects)}>
                  {option.effects.slice(0, 4).map((effect) => (
                    <span key={`${effect.line}:${effect.label}`} className="lc-choice-route__chip">
                      {effectDisplayLabel(effect, tr)}
                    </span>
                  ))}
                </span>
              ) : null}
            </button>
            {active && renderEditor ? (
              <div className="lc-choice-route__editor">
                {renderEditor(option.line)}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function GateRouteList({
  gate,
  choices = [],
  selectedLine,
  onSelectLine,
  renderEditor,
}: {
  gate: GateStructure;
  choices?: readonly ChoiceStructure[];
  selectedLine?: number | null;
  onSelectLine: (line: number) => void;
  renderEditor?: (line: number) => ReactNode;
}) {
  const tr = useTr();
  return (
    <ol className="lc-gate-routes" aria-label={tr(S.routeBlock)}>
      {gate.routes.map((route) => {
        const active = selectedLine === route.line;
        const condition = routeConditionText(route, choices, tr(S.fallback), tr(S.direct));
        const logic = route.isFallback ? tr(S.fallback) : route.index === 0 ? tr(S.ifRoute) : tr(S.elseIfRoute);
        return (
          <li key={`${route.index}:${route.raw}`} className="lc-gate-routes__item">
            <button
              type="button"
              className="lc-gate-route"
              data-active={active || undefined}
              aria-expanded={active ? "true" : "false"}
              onClick={(event) => {
                event.stopPropagation();
                onSelectLine(route.line);
              }}
            >
              <span className="lc-gate-route__index">{route.index + 1}</span>
              <span className="lc-gate-route__logic">{logic}</span>
              <span className="lc-gate-route__condition">
                {condition}
              </span>
              <span className="lc-gate-route__arrow" aria-hidden="true">→</span>
              <span className="lc-gate-route__outcome">
                {route.outcome.kind === "next" ? `${tr(S.routeTo)} ${outcomeLabel(route)}` : outcomeLabel(route)}
              </span>
            </button>
            {active && renderEditor ? (
              <div className="lc-gate-route__editor">
                {renderEditor(route.line)}
              </div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

// ── public surface ────────────────────────────────────────────────────────────

function NodePalette({
  source,
  selectedLine,
  onInsert,
}: {
  source: string;
  selectedLine?: number | null;
  onInsert: (kind: InsertKind, afterLine?: number | null) => void;
}) {
  const tr = useTr();
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<"root" | "minigame">("root");
  const blocks = useMemo(() => parseLsBlocks(source), [source]);
  const hasGate = blocks.some((b) => b.kind === "gate");
  const selectedBlock = selectedLine ? blocks.find((b) => b.line === selectedLine) : undefined;
  const selectedBlockIndex = selectedLine ? blocks.findIndex((b) => b.line === selectedLine) : -1;
  const selectedHasAncestor = (kind: LsBlock["kind"]): boolean => {
    const block = selectedBlockIndex >= 0 ? blocks[selectedBlockIndex] : undefined;
    if (!block) return false;
    for (let i = selectedBlockIndex - 1; i >= 0; i--) {
      const candidate = blocks[i]!;
      if (candidate.depth >= block.depth) continue;
      if (candidate.kind === kind) return true;
    }
    return false;
  };

  const disabledReason = (kind: InsertKind): string | undefined => {
    if (kind === "choice" && selectedBlock && (selectedHasAncestor("choice") || selectedHasAncestor("gate"))) return tr(S.choiceNested);
    if (kind === "gate" && hasGate) return tr(S.gateExists);
    return undefined;
  };

  const label = selectedLine ? tr(S.addAfterSelected) : tr(S.addNode);
  const startDrag = (event: DragEvent<HTMLButtonElement>, kind: InsertKind) => {
    event.dataTransfer.setData(INSERT_DRAG_MIME, kind);
    event.dataTransfer.effectAllowed = "copy";
  };
  const pick = (kind: InsertKind) => {
    onInsert(kind, selectedLine);
    setOpen(false);
    setPanel("root");
  };

  useEffect(() => {
    if (!open) setPanel("root");
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <aside
      className="lc-canvas-palette nowheel nopan nodrag"
      aria-label={tr(S.addDockTitle)}
      data-open={open || undefined}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div className="lc-canvas-palette__head">
        <button
          type="button"
          className="lc-canvas-palette__label"
          onClick={() => pick("narration")}
        >
          <Plus size={14} aria-hidden="true" />
          {label}
        </button>
        <button
          type="button"
          className="lc-canvas-palette__menu"
          aria-label={tr(S.addDockTitle)}
          aria-expanded={open ? "true" : "false"}
          onClick={() => setOpen((value) => !value)}
        >
          <SlidersHorizontal size={14} aria-hidden="true" />
        </button>
      </div>
      {open && panel === "root" ? (
        <div className="lc-canvas-palette__quick" aria-label={tr(S.addDockTitle)}>
          {QUICK_INSERT_KINDS.map((kind) => {
            const opt = INSERT_OPTIONS_BY_KIND.get(kind);
            if (!opt) return null;
            const reason = disabledReason(opt.kind);
            return (
              <button
                key={opt.kind}
                type="button"
                className="lc-canvas-palette__quick-item"
                disabled={Boolean(reason)}
                title={reason ?? tr(opt.label)}
                draggable={!reason}
                onDragStart={(event) => startDrag(event, opt.kind)}
                onClick={() => pick(opt.kind)}
                style={{ ["--lc-kind" as string]: `var(${opt.token})` }}
              >
                <span className="lc-canvas-palette__swatch" aria-hidden="true" />
                <span>{tr(opt.label)}</span>
              </button>
            );
          })}
        </div>
      ) : null}
      {panel === "minigame" ? (
        <div className="lc-canvas-palette__groups">
          <section className="lc-canvas-palette__group" aria-label={tr(S.chooseMinigame)}>
            <button type="button" className="lc-canvas-palette__back" onClick={() => setPanel("root")}>
              ← {tr(S.backToAddMenu)}
            </button>
            <span className="lc-canvas-palette__group-label">{tr(S.chooseMinigame)}</span>
            <div className="lc-canvas-palette__items">
              {minigamePresetInsertOptions().map((opt) => {
                const reason = disabledReason(opt.kind);
                return (
                  <button
                    key={opt.kind}
                    type="button"
                    className="lc-canvas-palette__item"
                    disabled={Boolean(reason)}
                    title={reason ?? tr(opt.label)}
                    draggable={!reason}
                    onDragStart={(event) => startDrag(event, opt.kind)}
                    onClick={() => pick(opt.kind)}
                    style={{ ["--lc-kind" as string]: `var(${opt.token})` }}
                  >
                    <span className="lc-canvas-palette__swatch" aria-hidden="true" />
                    <span>{tr(opt.label)}</span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      ) : (
        <div className="lc-canvas-palette__groups">
          {PALETTE_GROUPS.map((group) => (
            <section key={tr(group.label)} className="lc-canvas-palette__group" aria-label={tr(group.label)}>
              <span className="lc-canvas-palette__group-label">{tr(group.label)}</span>
              <div className="lc-canvas-palette__items">
                {group.kinds.map((kind) => {
                  const opt = INSERT_OPTIONS_BY_KIND.get(kind);
                  if (!opt) return null;
                  const reason = disabledReason(opt.kind);
                  const isSubmenu = opt.kind === "minigame";
                  return (
                    <button
                      key={opt.kind}
                      type="button"
                      className="lc-canvas-palette__item"
                      disabled={Boolean(reason)}
                      title={reason ?? tr(opt.label)}
                      draggable={!reason && !isSubmenu}
                      onDragStart={isSubmenu ? undefined : (event) => startDrag(event, opt.kind)}
                      onClick={() => {
                        if (isSubmenu) setPanel("minigame");
                        else pick(opt.kind);
                      }}
                      style={{ ["--lc-kind" as string]: `var(${opt.token})` }}
                    >
                      <span className="lc-canvas-palette__swatch" aria-hidden="true" />
                      <span>{tr(opt.label)}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </aside>
  );
}

function CanvasValidationBadge({ validation }: { validation?: CanvasValidationState }) {
  const tr = useTr();
  const status = validation?.status ?? "idle";
  if (status === "checking") {
    return (
      <Badge size="sm" variant="tonal" tone="neutral">
        <Spinner size="sm" /> {tr(S.checkRunning)}
      </Badge>
    );
  }
  if (status === "valid") {
    return (
      <Badge size="sm" variant="tonal" tone="success">
        <CircleCheck size={13} aria-hidden="true" /> {tr(S.checkValid)}
      </Badge>
    );
  }
  if (status === "invalid") {
    return (
      <Badge size="sm" variant="tonal" tone="danger">
        <TriangleAlert size={13} aria-hidden="true" /> {tr(S.checkInvalid)}
      </Badge>
    );
  }
  if (status === "unavailable") {
    return (
      <Badge size="sm" variant="tonal" tone="neutral">
        {tr(S.checkUnavailable)}
      </Badge>
    );
  }
  return (
    <Badge size="sm" variant="tonal" tone="neutral">
      {tr(S.checkIdle)}
    </Badge>
  );
}

function CanvasValidationPanel({ validation }: { validation?: CanvasValidationState }) {
  const tr = useTr();
  const diagnostics = validation?.status === "invalid" ? (validation.diagnostics ?? []) : [];
  if (diagnostics.length === 0) return null;
  return (
    <div className="lc-canvas-validation" role="alert">
      <div className="lc-canvas-validation__head">
        <TriangleAlert size={14} aria-hidden="true" />
        <span>{diagnostics.length === 1 ? diagnostics[0] : tr(S.diagnosticsCount, { count: diagnostics.length })}</span>
      </div>
      {diagnostics.length > 1 ? (
        <ul className="lc-canvas-validation__list">
          {diagnostics.map((diagnostic, index) => (
            <li key={`${index}:${diagnostic}`}>{diagnostic}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function EpisodeModeBar({
  mode,
  activeTitle,
  onModeChange,
}: {
  mode: CanvasMode;
  activeTitle: string;
  onModeChange: (mode: CanvasMode) => void;
}) {
  const tr = useTr();
  return (
    <div className="lc-canvas-modebar" aria-label={tr(S.visualEditor)}>
      <div className="lc-canvas-modebar__tabs" aria-label={tr(S.visualEditor)}>
        <Button
          className="lc-canvas-modebar__tab"
          variant={mode === "story" ? "secondary" : "ghost"}
          size="sm"
          leadingIcon={<MapIcon size={15} aria-hidden />}
          aria-pressed={mode === "story"}
          onClick={() => onModeChange("story")}
        >
          {tr(S.modeStory)}
        </Button>
        <Button
          className="lc-canvas-modebar__tab"
          variant={mode === "episode" ? "secondary" : "ghost"}
          size="sm"
          leadingIcon={<Clapperboard size={15} aria-hidden />}
          aria-pressed={mode === "episode"}
          onClick={() => onModeChange("episode")}
        >
          {tr(S.modeEpisode)}
        </Button>
      </div>
      <p className="lc-canvas-modebar__copy">
        {mode === "story" ? tr(S.modeStoryHint) : `${tr(S.storyboardOf, { title: activeTitle })} · ${tr(S.modeEpisodeHint)}`}
      </p>
    </div>
  );
}

function CanvasSelectedRows({
  visual,
  source,
  allBlocks,
  onChange,
  selectedLine,
  onSelectLine,
  renderEditor,
  suppressInlineEditorLines,
  editable = false,
}: {
  visual: EpisodeVisualBlock;
  source: string;
  allBlocks: readonly LsBlock[];
  onChange: (source: string) => void;
  selectedLine?: number | null;
  onSelectLine: (line: number) => void;
  renderEditor?: (block: LsBlock) => ReactNode;
  suppressInlineEditorLines?: ReadonlySet<number>;
  editable?: boolean;
}) {
  const tr = useTr();
  const canEditRows = editable && !visual.choice && !visual.gate;
  const sortableItems = useMemo(() => visual.blocks.map((block) => `canvas-step:${block.line}`), [visual.blocks]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const insertAfterLine = useCallback(
    (line: number, kind: InsertKind) => {
      const result = insertBlockAfterLineWithResult(source, allBlocks, line, kind);
      if (result.source !== source) onChange(result.source);
      if (result.insertedLine != null) onSelectLine(result.insertedLine);
    },
    [allBlocks, onChange, onSelectLine, source],
  );

  const splitBeforeLine = useCallback(
    (line: number) => {
      const result = splitCanvasBlockBeforeLineWithResult(source, line);
      if (result.source !== source) onChange(result.source);
      if (result.insertedLine != null) onSelectLine(result.insertedLine);
    },
    [onChange, onSelectLine, source],
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const activeId = String(event.active.id);
      const overId = event.over ? String(event.over.id) : "";
      if (!activeId || !overId || activeId === overId) return;
      const fromLine = Number(activeId.replace("canvas-step:", ""));
      const toLine = Number(overId.replace("canvas-step:", ""));
      if (!Number.isFinite(fromLine) || !Number.isFinite(toLine)) return;
      const fromIndex = visual.blocks.findIndex((block) => block.line === fromLine);
      const toIndex = visual.blocks.findIndex((block) => block.line === toLine);
      const placement = fromIndex >= 0 && toIndex >= 0 && fromIndex < toIndex ? "after" : "before";
      const result = moveSiblingBlockByLine(source, allBlocks, fromLine, toLine, placement);
      if (result.changed) {
        onChange(result.source);
        onSelectLine(result.movedLine ?? fromLine);
      }
    },
    [allBlocks, onChange, onSelectLine, source, visual.blocks],
  );

  const renderRow = (block: LsBlock) => (
    <CanvasSelectedStepRow
      key={`${block.line}:${block.raw}`}
      block={block}
      active={block.line === selectedLine}
      editable={canEditRows}
      canSplit={canEditRows && block.line !== visual.line}
      sortableId={`canvas-step:${block.line}`}
      onInsertAfter={(kind) => insertAfterLine(block.line, kind)}
      onSplitBefore={() => splitBeforeLine(block.line)}
      onSelect={() => onSelectLine(block.line)}
      renderEditor={renderEditor}
      source={source}
      onChange={onChange}
      suppressInlineEditor={Boolean(suppressInlineEditorLines?.has(block.line))}
    />
  );

  const rows = (
    <div className="lc-canvas-selected__rows" role="list" aria-label={tr(S.stepCount, { count: visual.blocks.length })}>
      {groupConcurrentBlocks(visual.blocks).map((item, index) => {
        if (!isConcurrentBlockGroup(item)) return renderRow(item);
        return (
          <div key={`concurrent:${index}:${item[0]?.line ?? index}`} className="lc-canvas-selected__concurrent" role="group" aria-label={tr(S.concurrent)}>
            <span className="lc-canvas-selected__concurrent-label">{tr(S.concurrent)}</span>
            <div className="lc-canvas-selected__concurrent-rows">
              {item.map((block) => renderRow(block))}
            </div>
          </div>
        );
      })}
    </div>
  );

  if (canEditRows) {
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
          {rows}
        </SortableContext>
      </DndContext>
    );
  }

  return rows;
}

function CanvasSelectedStepRow({
  block,
  active,
  editable,
  canSplit,
  sortableId,
  onInsertAfter,
  onSplitBefore,
  onSelect,
  renderEditor,
  source,
  onChange,
  suppressInlineEditor,
}: {
  block: LsBlock;
  active: boolean;
  editable: boolean;
  canSplit: boolean;
  sortableId: string;
  onInsertAfter: (kind: InsertKind) => void;
  onSplitBefore: () => void;
  onSelect: () => void;
  renderEditor?: (block: LsBlock) => ReactNode;
  source: string;
  onChange: (source: string) => void;
  suppressInlineEditor: boolean;
}) {
  const tr = useTr();
  const sortable = useSortable({ id: sortableId, disabled: !editable });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
  const style: CSSProperties = {
    ["--lc-kind" as string]: `var(${kindToken(block.kind)})`,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.48 } : {}),
  };

  return (
    <div
      ref={setNodeRef}
      className="lc-canvas-selected__row"
      data-active={active || undefined}
      data-editable={editable || undefined}
      data-dragging={isDragging || undefined}
      role="listitem"
      style={style}
    >
      {editable ? (
        <div className="lc-canvas-selected__gutter">
          <button
            type="button"
            className="lc-canvas-selected__grip"
            aria-label={tr(S.dragStepToReorder)}
            title={tr(S.dragStepToReorder)}
            {...attributes}
            {...listeners}
          >
            <Move size={13} aria-hidden="true" />
          </button>
          <AddBlockMenu variant="gutter" label={tr(S.insertAfterBlock)} onPick={onInsertAfter} />
          {canSplit ? (
            <button
              type="button"
              className="lc-canvas-selected__split"
              aria-label={tr(S.splitHere)}
              title={tr(S.splitHere)}
              onClick={(event) => {
                event.stopPropagation();
                onSplitBefore();
              }}
            >
              <Scissors size={13} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="lc-canvas-selected__row-main">
        <button
          type="button"
          className="lc-canvas-selected__step"
          data-active={active || undefined}
          aria-current={active ? "true" : undefined}
          aria-expanded={active ? "true" : "false"}
          onClick={onSelect}
        >
          <span className="lc-canvas-selected__step-kind">
            {kindLabel(block.kind)}
            {block.concurrent ? <span className="lc-canvas-selected__step-chip">{tr(S.concurrent)}</span> : null}
          </span>
          <span className="lc-canvas-selected__step-text">{describeBlock(block)}</span>
          <span className="lc-canvas-selected__step-line">{tr(S.sourceLine, { line: block.line })}</span>
        </button>
        {active && !suppressInlineEditor ? (
          <div className="lc-canvas-selected__inline-editor">
            {renderEditor ? renderEditor(block) : <BlockFieldsEditor source={source} block={block} onChange={onChange} />}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function selectedOptionForLine(choice: ChoiceStructure, selectedLine?: number | null): ChoiceOptionStructure | undefined {
  if (selectedLine == null) return undefined;
  return choice.options.find((option) => selectedLine === option.line || (selectedLine > option.range.start && selectedLine < option.range.end));
}

function ChoiceOptionBodyEditor({
  option,
  source,
  allBlocks,
  onChange,
  selectedLine,
  onSelectLine,
  renderEditor,
}: {
  option: ChoiceOptionStructure;
  source: string;
  allBlocks: readonly LsBlock[];
  onChange: (source: string) => void;
  selectedLine?: number | null;
  onSelectLine: (line: number) => void;
  renderEditor?: (block: LsBlock) => ReactNode;
}) {
  const tr = useTr();
  const bodyBlocks = useMemo(
    () => blocksInsideRange(allBlocks, option.range).filter((block) => block.kind !== "param"),
    [allBlocks, option.range],
  );
  const insertInsideOption = useCallback(
    (kind: InsertKind) => {
      const result = insertBlockInsideOptionWithResult(source, allBlocks, option.line, kind);
      if (result.source !== source) onChange(result.source);
      if (result.insertedLine != null) onSelectLine(result.insertedLine);
    },
    [allBlocks, onChange, onSelectLine, option.line, source],
  );

  if (bodyBlocks.length === 0) {
    return (
      <section className="lc-canvas-option-body" aria-label={tr(S.optionBranch)}>
        <header className="lc-canvas-option-body__head">
          <span>{tr(S.optionBranch)}</span>
          <AddBlockMenu variant="button" label={tr(S.addOptionBranchContent)} onPick={insertInsideOption} />
        </header>
        <p className="lc-canvas-option-body__empty">{tr(S.emptyOptionBranch)}</p>
      </section>
    );
  }

  const visual: EpisodeVisualBlock = {
    id: `option-body:${option.line}`,
    kind: "narrative",
    title: option.text || option.id || tr(S.optionBranch),
    summary: bodyBlocks.slice(0, 3).map((block) => clipText(describeBlock(block), 34)).join(" / "),
    line: bodyBlocks[0]?.line ?? option.line,
    endLine: bodyBlocks[bodyBlocks.length - 1]?.line ?? option.line,
    blocks: bodyBlocks,
  };

  return (
    <section className="lc-canvas-option-body" aria-label={tr(S.optionBranch)}>
      <header className="lc-canvas-option-body__head">
        <span>{tr(S.optionBranch)}</span>
        <AddBlockMenu variant="button" label={tr(S.addOptionBranchContent)} onPick={insertInsideOption} />
      </header>
      <CanvasSelectedRows
        visual={visual}
        source={source}
        allBlocks={allBlocks}
        onChange={onChange}
        selectedLine={selectedLine}
        onSelectLine={onSelectLine}
        renderEditor={renderEditor}
        editable
      />
    </section>
  );
}

function CanvasInspector({
  activeId,
  source,
  onChange,
  selectedLine,
  onSelectLine,
  onClose,
  validation,
  sourceReady,
  structure,
}: {
  activeId?: string;
  source: string;
  onChange: (source: string) => void;
  selectedLine?: number | null;
  onSelectLine: (line: number) => void;
  onClose?: () => void;
  validation?: CanvasValidationState;
  sourceReady: boolean;
  structure?: EpisodeStructure;
}) {
  const tr = useTr();
  const blocks = structure?.blocks ?? parseLsBlocks(source);
  const visualBlocks = useMemo(() => buildEpisodeVisualBlocks(blocks, structure, source), [blocks, source, structure]);
  const blockByLine = useMemo(() => new Map(blocks.map((block) => [block.line, block])), [blocks]);
  const structureByLine = useMemo(() => {
    const choices = new Map<number, ChoiceStructure>();
    const options = new Map<number, ChoiceOptionStructure>();
    for (const choice of structure?.choices ?? []) {
      choices.set(choice.line, choice);
      for (const option of choice.options) {
        options.set(option.line, option);
      }
    }
    return {
      choices,
      options,
      gate: structure?.gate,
      allChoices: structure?.choices ?? [],
    };
  }, [structure]);
  const selectedVisual = visualBlocks.find((block) => lineInVisualBlock(selectedLine, block));
  const selectedChoiceOption = selectedVisual?.choice ? selectedOptionForLine(selectedVisual.choice, selectedLine) : undefined;
  const renderBlockEditor = useCallback(
    (block: LsBlock): ReactNode => (
      <BlockFieldsEditor
        source={source}
        block={block}
        onChange={onChange}
        choice={structureByLine.choices.get(block.line)}
        option={structureByLine.options.get(block.line)}
        gate={structureByLine.gate?.line === block.line ? structureByLine.gate : undefined}
        allChoices={structureByLine.allChoices}
      />
    ),
    [onChange, source, structureByLine],
  );
  const renderLineEditor = useCallback(
    (line: number): ReactNode => {
      const block = blockByLine.get(line);
      return block ? renderBlockEditor(block) : null;
    },
    [blockByLine, renderBlockEditor],
  );
  const suppressInlineEditorLines = useMemo(() => {
    const set = new Set<number>();
    if (selectedVisual?.choice?.options.some((option) => option.line === selectedLine)) set.add(selectedLine!);
    if (selectedVisual?.gate?.routes.some((route) => route.line === selectedLine)) set.add(selectedLine!);
    return set;
  }, [selectedLine, selectedVisual]);
  const meta = useMemo(() => {
    const episode = blocks.find((b) => b.kind === "episode");
    return episode && episode.kind === "episode"
      ? { title: episode.title, branchKey: episode.branchKey }
      : {};
  }, [blocks]);

  return (
    <aside
      className="lc-canvas-inspector nopan nodrag nowheel"
      aria-label={tr(S.activeScene)}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <header className="lc-canvas-inspector__head">
        <div className="lc-canvas-inspector__badges">
          <Badge size="sm" variant="tonal" tone="neutral">
            <PencilLine size={13} aria-hidden="true" /> {tr(S.visualEditor)}
          </Badge>
          <CanvasValidationBadge validation={validation} />
        </div>
        <div>
          <h2 className="lc-canvas-inspector__title">{meta.title || activeId || tr(S.activeScene)}</h2>
          {meta.branchKey ? <p className="lc-canvas-inspector__id">{meta.branchKey}</p> : null}
        </div>
        {onClose ? (
          <IconButton label={tr(S.closeInspector)} size="sm" onClick={onClose}>
            <X size={15} aria-hidden="true" />
          </IconButton>
        ) : null}
      </header>
      <div className="lc-canvas-inspector__body">
        <CanvasValidationPanel validation={validation} />
        {!sourceReady ? (
          <div className="lc-canvas-selected lc-canvas-selected--empty" role="status" aria-live="polite">
            <span className="lc-episode-board__loading">
              <Spinner size="sm" />
              {tr(S.loadingScene)}
            </span>
          </div>
        ) : selectedVisual ? (
          <section className="lc-canvas-selected" aria-label={tr(S.selectedBlock)}>
            <header className="lc-canvas-selected__head">
              <div>
                <span
                  className="lc-canvas-selected__type"
                  style={{ ["--lc-kind" as string]: visualToneFor(selectedVisual.kind) }}
                >
                  {iconForVisualKind(selectedVisual.kind)}
                  {tr(visualKindLabel(selectedVisual.kind))}
                </span>
                <h3 className="lc-canvas-selected__title">{selectedVisual.title}</h3>
              </div>
              <Badge size="sm" variant="tonal" tone="neutral">
                {tr(S.sourceRange, { start: selectedVisual.line, end: selectedVisual.endLine })}
              </Badge>
            </header>
            <div className="lc-canvas-selected__steps" role="list" aria-label={tr(S.stepCount, { count: selectedVisual.blocks.length })}>
              {selectedVisual.choice ? (
                <ChoiceRouteList
                  choice={selectedVisual.choice}
                  selectedLine={selectedLine}
                  onSelectLine={onSelectLine}
                  renderEditor={renderLineEditor}
                />
              ) : selectedVisual.gate ? (
                <GateRouteList
                  gate={selectedVisual.gate}
                  choices={structure?.choices ?? []}
                  selectedLine={selectedLine}
                  onSelectLine={onSelectLine}
                  renderEditor={renderLineEditor}
                />
              ) : null}
            </div>
            {selectedVisual.choice && selectedChoiceOption ? (
              <ChoiceOptionBodyEditor
                option={selectedChoiceOption}
                source={source}
                allBlocks={blocks}
                onChange={onChange}
                selectedLine={selectedLine}
                onSelectLine={onSelectLine}
                renderEditor={renderBlockEditor}
              />
            ) : null}
            {!selectedVisual.choice && !selectedVisual.gate ? (
              <CanvasSelectedRows
                visual={selectedVisual}
                source={source}
                allBlocks={blocks}
                onChange={onChange}
                selectedLine={selectedLine}
                onSelectLine={onSelectLine}
                renderEditor={renderBlockEditor}
                suppressInlineEditorLines={suppressInlineEditorLines}
                editable
              />
            ) : null}
          </section>
        ) : null}
      </div>
    </aside>
  );
}

export function CanvasView({
  scenes,
  activeId,
  onSelect,
  activeSource,
  activeSourceId,
  onActiveSourceChange,
  onInsertBlock,
  onUndoSourceChange,
  canUndoSourceChange,
  onCreateScene,
  validation,
  selectedLine: selectedLineProp,
  onSelectedLineChange,
  initialMode = "story",
}: CanvasViewProps) {
  const tr = useTr();
  const [mode, setMode] = useState<CanvasMode>(initialMode);
  const [localSelectedLine, setLocalSelectedLine] = useState<number | null>(null);
  const studioRef = useRef<HTMLDivElement>(null);
  const selectedLine = selectedLineProp ?? localSelectedLine;
  const editable = activeSource !== undefined && Boolean(onActiveSourceChange);
  const sourceReady = editable && (activeSourceId === undefined || activeSourceId === activeId);
  const activeStructure = useMemo(() => (sourceReady ? buildEpisodeStructure(activeSource ?? "") : undefined), [activeSource, sourceReady]);
  const activeBlocks = activeStructure?.blocks ?? [];
  const activeTitle = useMemo(() => {
    const episode = activeBlocks.find((block) => block.kind === "episode");
    return episode && episode.kind === "episode" ? (episode.title || episode.branchKey || activeId || tr(S.activeScene)) : activeId || tr(S.activeScene);
  }, [activeBlocks, activeId, tr]);

  const selectLine = useCallback(
    (line: number | null) => {
      setLocalSelectedLine(line);
      onSelectedLineChange?.(line);
    },
    [onSelectedLineChange],
  );

  useEffect(() => {
    selectLine(null);
  }, [activeId, selectLine]);

  useEffect(() => {
    if (!editable) setMode("story");
  }, [editable]);

  const openEpisode = useCallback(
    (sceneId: string) => {
      onSelect?.(sceneId);
      if (editable) setMode("episode");
    },
    [editable, onSelect],
  );

  const focusCanvasShortcuts = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || isTextEditingTarget(target)) return;
    if (target.closest("button,a,[role='button'],.lc-flow-insert-menu,.lc-canvas-palette,.lc-canvas-floating-inspector")) return;
    studioRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const clearCanvasShortcutFocus = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest(".lc-canvas-studio")) return;
      if (document.activeElement === studioRef.current) studioRef.current?.blur();
    };
    window.addEventListener("pointerdown", clearCanvasShortcutFocus, true);
    return () => window.removeEventListener("pointerdown", clearCanvasShortcutFocus, true);
  }, []);

  const storyMap = (
    <ReactFlowProvider key="story">
      <StoryLevel
        scenes={scenes}
        activeId={activeId}
        onSelect={onSelect}
        onDrill={openEpisode}
        canDrill={editable}
        onCreateMissing={onCreateScene}
      />
    </ReactFlowProvider>
  );

  const episodeMap = (
    sourceReady ? (
      <ReactFlowProvider key="episode">
        <EpisodeFlowLevel
          scenes={scenes}
          blocks={activeBlocks}
          source={activeSource ?? ""}
          onChange={onActiveSourceChange}
          structure={activeStructure}
          selectedLine={selectedLine}
          onSelectLine={(line) => selectLine(line)}
          onInsertBlock={onInsertBlock}
          onUndoSourceChange={onUndoSourceChange}
          canUndoSourceChange={canUndoSourceChange}
          onCreateScene={onCreateScene}
          activeId={activeId}
        />
      </ReactFlowProvider>
    ) : (
      <EpisodeLoadingBoard />
    )
  );

  const map = (
    <div className="lc-canvas-shell">
      {mode === "episode" && editable ? episodeMap : storyMap}
    </div>
  );

  if (!editable) return map;

  return (
    <div
      ref={studioRef}
      className="lc-canvas-studio"
      data-inspector-open={mode === "episode" && sourceReady && selectedLine ? "true" : undefined}
      tabIndex={-1}
      onPointerDown={focusCanvasShortcuts}
    >
      <section className="lc-canvas-studio__main">
        <EpisodeModeBar
          mode={mode}
          activeTitle={activeTitle}
          onModeChange={(nextMode) => {
            setMode(nextMode);
            selectLine(null);
          }}
        />
        <div className="lc-canvas-studio__map">
          {map}
        </div>
        {mode === "episode" && sourceReady && onInsertBlock ? (
          <NodePalette source={activeSource} selectedLine={selectedLine} onInsert={onInsertBlock} />
        ) : null}
      </section>
      {mode === "episode" && sourceReady && selectedLine ? (
        <div className="lc-canvas-floating-inspector">
          <CanvasInspector
            activeId={activeId}
            source={activeSource}
            onChange={onActiveSourceChange!}
            selectedLine={selectedLine}
            onSelectLine={(line) => selectLine(line)}
            onClose={() => selectLine(null)}
            validation={validation}
            sourceReady={sourceReady}
            structure={activeStructure}
          />
        </div>
      ) : null}
    </div>
  );
}
